import { getFeltColor, setFeltColor } from '../render/theme.js';
import {
  applyTableCondition,
  setRackSpread,
  clearBreakCuePos,
  type PlayMode,
} from '../sim/world.js';
import { createRackEditor } from './rack-editor.js';
import {
  chatTts,
  CUSTOM_PITCH_MIN,
  CUSTOM_PITCH_MAX,
  CUSTOM_RATE_MIN,
  CUSTOM_RATE_MAX,
  CUSTOM_PRESET_LABEL,
  type Gender,
  type PresetId,
  type VoicePreset,
} from '../audio/chat-tts.js';

/**
 * 設定メニュー（自前・GPL非依存）。iOS 設定風のリストUI。
 *   - 浅い設定（ON/OFF）: 行内にトグルスイッチ（addToggleRow）。
 *   - 深い設定（サブ画面あり）: 行に現在値＋シェブロンを表示し、タップでサブ画面へ
 *     ドリルダウン（addDrillRow + ページ切替）。
 * createSettings() はパネルを構築し、開閉用の toggle を返す。ボタン設置は呼び出し側。
 * 項目追加は addToggleRow / addDrillRow を増やすだけ。
 */

// localStorage 上の真偽設定を読む（未設定なら既定値）／書く。
const loadBool = (key: string, def: boolean): boolean => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? def : v === '1';
  } catch {
    return def;
  }
};
const saveBool = (key: string, v: boolean): void => {
  try {
    localStorage.setItem(key, v ? '1' : '0');
  } catch (err) {
    console.error(`${key}: localStorage 保存失敗:`, err);
  }
};
const loadNumber = (key: string, def: number): number => {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return def;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : def;
  } catch {
    return def;
  }
};
const saveNumber = (key: string, v: number): void => {
  try {
    localStorage.setItem(key, String(v));
  } catch (err) {
    console.error(`${key}: localStorage 保存失敗:`, err);
  }
};

// 「ショット後にトップビュー」設定（既定 OFF）。main.ts が発射時に参照する。
// ONで真上トップビューに自動切替（ショット中の球の動きが追いやすい）。OFFはフォローカメラ。
// 既定 OFF: フォローカメラ（ダイナミック・フォローフレーミング）を既定体験とする。
const AUTO_TOP_KEY = 'pool.autoTopAfterShot';
let autoTopAfterShot = loadBool(AUTO_TOP_KEY, false);
export const isAutoTopAfterShot = (): boolean => autoTopAfterShot;
const setAutoTopAfterShot = (v: boolean): void => {
  autoTopAfterShot = v;
  saveBool(AUTO_TOP_KEY, v);
};

// 「ビューアングル自動リセット」設定（既定 OFF / 秒数候補 0.5 秒）。controls.ts が orbit/zoom 操作後に
// 参照し、最後のユーザー view 操作から N 秒経過したら自動で初期構図へ滑らかに戻す。
// 秒数候補は 0.5 / 1 / 2 / 3 秒（離散値）。トップビュー中のピンチ/パンは対象外。
// 既定では仰角・方位のみ復元（ズーム=半径は維持）。「ズームも含む」を ON で半径も復元。
// OFF（既定）時は手動復帰ボタン（controls.ts .pool-recenter）を画面左下に表示する。
const AUTO_VIEW_RESET_KEY = 'pool.autoViewReset';
// 既定 OFF。既存ユーザーは localStorage の保存値を維持（破壊的変更なし）。
let autoViewReset = loadBool(AUTO_VIEW_RESET_KEY, false);
export const isAutoViewReset = (): boolean => autoViewReset;
const setAutoViewReset = (v: boolean): void => {
  autoViewReset = v;
  saveBool(AUTO_VIEW_RESET_KEY, v);
};
const AUTO_VIEW_RESET_SEC_KEY = 'pool.autoViewResetSec';
const AUTO_VIEW_RESET_SEC_DEFAULT = 0.5;
const AUTO_VIEW_RESET_SEC_CHOICES: readonly number[] = [0.5, 1, 2, 3];
const formatViewResetSec = (n: number): string =>
  Number.isInteger(n) ? `${n} 秒` : `${n} 秒`;
const loadAutoViewResetSec = (): number => {
  const n = loadNumber(AUTO_VIEW_RESET_SEC_KEY, AUTO_VIEW_RESET_SEC_DEFAULT);
  return AUTO_VIEW_RESET_SEC_CHOICES.includes(n) ? n : AUTO_VIEW_RESET_SEC_DEFAULT;
};
let autoViewResetSec = loadAutoViewResetSec();
export const getAutoViewResetSec = (): number => autoViewResetSec;
const setAutoViewResetSec = (v: number): void => {
  if (!AUTO_VIEW_RESET_SEC_CHOICES.includes(v)) return;
  autoViewResetSec = v;
  saveNumber(AUTO_VIEW_RESET_SEC_KEY, v);
};
// 自動リセットでズーム（カメラ半径）も初期値へ戻すか（既定 OFF）。
// OFF: 仰角・方位だけ戻し、ズームは維持（拡大したままショットしたい場合に対応）。
// ON: 仰角・方位＋ズームを全て初期値へ戻す。
const AUTO_VIEW_RESET_ZOOM_KEY = 'pool.autoViewResetZoom';
let autoViewResetZoom = loadBool(AUTO_VIEW_RESET_ZOOM_KEY, false);
export const isAutoViewResetZoom = (): boolean => autoViewResetZoom;
const setAutoViewResetZoom = (v: boolean): void => {
  autoViewResetZoom = v;
  saveBool(AUTO_VIEW_RESET_ZOOM_KEY, v);
};

// ショット系UI「操作中の一時非表示」遅延（既定 800ms）。
// 「方向アロー・ズーム・カメラオービット」を操作中は撞点パッド・仰角・縦キュー・退避タブを
// 自動的に display:none で隠し、操作完了から本値の遅延後に復帰する。
// 目的: ポケットや球がこれらUIに隠れる/ショット力UI誤操作を防止。
// 候補: 0=OFF（隠さない）/ 250 / 500 / 800 / 1200 ms（5 段）。
// 関連: src/ui/shot-ui-temp-hide.ts（純粋関数）・src/ui/controls.ts（DOM 配線）。
const SHOT_UI_TEMP_HIDE_KEY = 'pool.shotUiTempHideMs';
const SHOT_UI_TEMP_HIDE_DEFAULT = 800;
const SHOT_UI_TEMP_HIDE_CHOICES: readonly number[] = [0, 250, 500, 800, 1200];
const formatShotUiTempHide = (n: number): string =>
  n === 0 ? 'OFF（隠さない）' : `${n} ms`;
const loadShotUiTempHide = (): number => {
  const n = loadNumber(SHOT_UI_TEMP_HIDE_KEY, SHOT_UI_TEMP_HIDE_DEFAULT);
  return SHOT_UI_TEMP_HIDE_CHOICES.includes(n) ? n : SHOT_UI_TEMP_HIDE_DEFAULT;
};
let shotUiTempHideMs = loadShotUiTempHide();
export const getShotUiTempHideMs = (): number => shotUiTempHideMs;
export const isShotUiTempHideEnabled = (): boolean => shotUiTempHideMs > 0;
const setShotUiTempHideMs = (v: number): void => {
  if (!SHOT_UI_TEMP_HIDE_CHOICES.includes(v)) return;
  shotUiTempHideMs = v;
  saveNumber(SHOT_UI_TEMP_HIDE_KEY, v);
};

// 「対象球が隠れたら自動トップビュー」設定（既定 ON）。
// 構え時に「次に狙うべき的玉」が他球の陰に隠れていたら真上ビューへ自動切替。
// ショットを撞いた瞬間に解除。次の構えで再判定。
const AUTO_TOP_OBSCURED_KEY = 'pool.autoTopOnObscured';
let autoTopOnObscured = loadBool(AUTO_TOP_OBSCURED_KEY, true);
export const isAutoTopOnObscured = (): boolean => autoTopOnObscured;
const setAutoTopOnObscured = (v: boolean): void => {
  autoTopOnObscured = v;
  saveBool(AUTO_TOP_OBSCURED_KEY, v);
};

// 「狙撃カム（ポケットイン演出）」設定（既定 OFF）。main.ts/camera-rig.ts が参照する。
// 発動条件: 手玉・的玉・狙うポケットの3点が構え時のビュー内に収まっている時のみ。
// 動作: ショット時のカメラ（位置・方位・仰角・ズーム）を**ポケットイン or 的玉クッション接触
// or 明らかに外れた瞬間**までフリーズ。解除後は通常の追従へスムーズに補間する。
// 「ショット後にトップビュー」とは**設定 UI 上で排他**（一方を ON にすると他方は自動 OFF）。
// 防御的に、両方 ON の状態でも本設定が autoTop を抑止する優先関係は維持する。
// 注意: 「明らかに外れ」判定は、的玉がヒット直後に進む方向と狙うポケットへの方位の差で判断する
// （閾値は SHOT_FREEZE_MISS_DEG=12°・shot-freeze-cam.ts の定数）。手玉クッション先撞きの
// 救済は採用しない（達成感維持のため、明確に外れた時のみ早期復帰する設計）。
const SHOT_FREEZE_CAM_KEY = 'pool.shotFreezeCam';
let shotFreezeCam = loadBool(SHOT_FREEZE_CAM_KEY, false);
export const isShotFreezeCam = (): boolean => shotFreezeCam;
const setShotFreezeCam = (v: boolean): void => {
  shotFreezeCam = v;
  saveBool(SHOT_FREEZE_CAM_KEY, v);
};
/** 狙撃カム ON 時の追加ズーム倍率（カメラ半径への乗数）。
 *  ⊕ボタン5回押下相当（ZOOM_STEP_IN=0.9 ＝ controls.ts と整合）。的玉を狙いやすくする目的で
 *  構え時のカメラ半径を縮める。main.ts の `computeAspectRadius()` が ON 時にこの値を乗じる。 */
export const SHOT_FREEZE_CAM_ZOOM_FACTOR = Math.pow(0.9, 5); // ≈ 0.59049

// 排他制約の起動時整合化: 過去ユーザーが「ショット後にトップビュー」と「狙撃カム」を
// 両方 ON で保存していた場合、狙撃カムを優先して autoTop を OFF に正規化する。
// 設定パネル側の排他制御（一方を ON にすると他方を OFF）と挙動を一致させる。
if (autoTopAfterShot && shotFreezeCam) {
  autoTopAfterShot = false;
  saveBool(AUTO_TOP_KEY, false);
}

// F-31 Broadcast カメラモード（既定 'off'・2026-06-26 (j) で 'director' から戻し）。
// 'off': 既存挙動（autoTop/autoViewReset/手動 Top/狙撃カム F-03 が個別に動作）。
// 'manual': プリセット 1〜3, 6 を HUD ピルバー＋キーボード 1〜6 で手動切替。
// 'director': Director 状態機械が自動カット。
// ON の間（manual/director）は、competing な既存自動演出設定行を disabled 表示にする。
// 設計書: knowledge/architecture/broadcast-camera-mode.md §1.2 / §11 変更履歴 2026-06-26 (j)
const BROADCAST_MODE_KEY = 'pool.broadcastMode';
type BroadcastModeValue = 'off' | 'manual' | 'director';
const BROADCAST_MODE_CHOICES: readonly BroadcastModeValue[] = [
  'off',
  'manual',
  'director',
];
// 新規ユーザー（localStorage 未設定）は **'off'** で起動する（P2b 実装は完了しているが、
// 実機での完成度がまだ低いため既定 ON を控える＝ユーザー要望 2026-06-26 (j)）。
// 既存の保存値（'manual' / 'director' を明示保存しているユーザー）は honor する。
// 完成度が上がったら 'director' に戻して新規ユーザーへ放送体験を自動提供する想定。
const BROADCAST_MODE_DEFAULT: BroadcastModeValue = 'off';
const loadBroadcastMode = (): BroadcastModeValue => {
  try {
    const v = localStorage.getItem(BROADCAST_MODE_KEY);
    if (v === 'off' || v === 'manual' || v === 'director') return v;
    return BROADCAST_MODE_DEFAULT;
  } catch {
    return BROADCAST_MODE_DEFAULT;
  }
};
let broadcastMode: BroadcastModeValue = loadBroadcastMode();
// F-31 P2b §12.7: 観戦モード Director 既定 ON — session override（永続化しない一時上書き）。
// main.ts の startSpectate 時に setBroadcastModeSessionOverride('director') を呼び、
// teardownMatch 時に null へ戻す。getBroadcastMode は override が立っていれば優先する
// （ただし設定 UI 表示は永続値 broadcastMode を見せ続けるため getBroadcastModePersisted を別途用意）。
let broadcastModeSessionOverride: BroadcastModeValue | null = null;
/** Broadcast モードの現在値（off/manual/director・session override 優先）。 */
export const getBroadcastMode = (): BroadcastModeValue =>
  broadcastModeSessionOverride ?? broadcastMode;
/** 永続保存値（設定 UI が「現在の保存値」を表示するための参照）。 */
export const getBroadcastModePersisted = (): BroadcastModeValue => broadcastMode;
/** Broadcast モードが ON か（manual or director・session override 優先）。 */
export const isBroadcastOn = (): boolean => getBroadcastMode() !== 'off';
const setBroadcastMode = (v: BroadcastModeValue): void => {
  if (!BROADCAST_MODE_CHOICES.includes(v)) return;
  broadcastMode = v;
  try {
    localStorage.setItem(BROADCAST_MODE_KEY, v);
  } catch (err) {
    console.error('broadcastMode: localStorage 保存失敗:', err);
  }
};
/** F-31 P2b §12.7: session override（観戦中の一時的な Director 既定 ON 用）。
 *  `null` を渡すと override 解除 → 永続値へ復帰。永続値は変更しない（localStorage 不変）。
 *  値が変わった場合は subscribeBroadcastModeChange に登録されたコールバックも呼ぶ。 */
export const setBroadcastModeSessionOverride = (
  v: BroadcastModeValue | null,
): void => {
  if (v !== null && !BROADCAST_MODE_CHOICES.includes(v)) return;
  const prev = getBroadcastMode();
  broadcastModeSessionOverride = v;
  const next = getBroadcastMode();
  if (prev !== next && onBroadcastModeChange) onBroadcastModeChange();
};

// F-31 P2b §12.7 観戦モード Director 既定 ON — 観戦突入時に getBroadcastMode()==='off'
// なら一時的に 'director' を適用するかどうかの設定。
// **既定 false（2026-06-26 (j) で true から戻し）**: メイン Broadcast 既定 OFF と整合性を取り、
// 完成度が低い現時点では観戦時にも Director を勝手に立ち上げない方針。完成度が上がったら
// true へ戻して放送体験を観戦時にも提供する。設定 UI から手動 ON できる。
const BROADCAST_SPECTATOR_DEFAULT_KEY = 'pool.broadcastSpectatorDefault';
let broadcastSpectatorDefault = loadBool(BROADCAST_SPECTATOR_DEFAULT_KEY, false);
export const isBroadcastSpectatorDefault = (): boolean =>
  broadcastSpectatorDefault;
const setBroadcastSpectatorDefault = (v: boolean): void => {
  broadcastSpectatorDefault = v;
  saveBool(BROADCAST_SPECTATOR_DEFAULT_KEY, v);
};

// F-31 P3.6 §14.16 カメラ切替 UI（HUD ピルバー）の表示 ON/OFF 設定（既定 OFF）。
// 既定 OFF の理由: Director 自動カット時はバーが情報量過多（"ビジー"）になり、
// プレイヤーが手動切替するケースも少ない。完成度が上がるまではバーを既定で隠す。
// **観戦中（spectating）は本設定に関わらず main.ts 側で強制表示**（観戦体験で
// 現在のカメラを示すには可視化が必要）。
const BROADCAST_BAR_VISIBLE_KEY = 'pool.broadcastBarVisible';
let broadcastBarVisible = loadBool(BROADCAST_BAR_VISIBLE_KEY, false);
export const isBroadcastBarVisible = (): boolean => broadcastBarVisible;
const setBroadcastBarVisible = (v: boolean): void => {
  broadcastBarVisible = v;
  saveBool(BROADCAST_BAR_VISIBLE_KEY, v);
  if (onBroadcastBarVisibilityChange) onBroadcastBarVisibilityChange();
};
/** main.ts が `pool.broadcastBarVisible` 設定変化を購読するためのコールバック。 */
let onBroadcastBarVisibilityChange: (() => void) | null = null;
export const subscribeBroadcastBarVisibilityChange = (cb: () => void): void => {
  onBroadcastBarVisibilityChange = cb;
};
const broadcastModeLabel = (): string => {
  if (broadcastMode === 'manual') return '手動切替（キー1〜6）';
  if (broadcastMode === 'director') return 'Director（自動カット）';
  return 'OFF';
};
/** main.ts が Broadcast モード切替に追従するためのコールバック登録。
 *  キーボード 1〜6 ハンドラ登録/解除や、現在プリセット適用の再評価に使う。 */
let onBroadcastModeChange: (() => void) | null = null;
/** main.ts から登録（モード切替時にプリセット再適用 or キーハンドラ更新を行う）。 */
export const subscribeBroadcastModeChange = (cb: () => void): void => {
  onBroadcastModeChange = cb;
};

// 「方向アロー・ズームボタン」表示設定（既定 ON）。
// 画面下の方向アロー（◀◀ ◀ ▶ ▶▶）と、タイミング撞き中に表示される
// 撞点パッド上のズーム⊕⊖ボタンを一括で表示/非表示する。
const CONTROL_UI_KEY = 'pool.controlUiVisible';
let controlUiVisible = loadBool(CONTROL_UI_KEY, true);
export const isControlUiVisible = (): boolean => controlUiVisible;
const setControlUiVisible = (v: boolean): void => {
  controlUiVisible = v;
  saveBool(CONTROL_UI_KEY, v);
  // body クラスで一括 CSS 制御（controls.ts は購読のみ）。
  document.body.classList.toggle('pool-no-control-ui', !v);
};
// 起動時に保存値を body へ反映（DOMContentLoaded 前後の競合を避けるためトップレベルで適用）。
document.body.classList.toggle('pool-no-control-ui', !controlUiVisible);

// 「効果音」設定（既定 ON）。main.ts が各イベントの発音可否に参照する。
const SOUND_KEY = 'pool.sound';
let soundEnabled = loadBool(SOUND_KEY, true);
export const isSoundEnabled = (): boolean => soundEnabled;
const setSoundEnabled = (v: boolean): void => {
  soundEnabled = v;
  saveBool(SOUND_KEY, v);
};

// 「残球表示」設定（既定 ON）。controls が左端のボールロスター（縦並び）の表示可否に参照する。
// どの球がポケットインしたかを可視化するオーバーレイ。
const BALL_ROSTER_KEY = 'pool.ballRoster';
let ballRoster = loadBool(BALL_ROSTER_KEY, true);
export const isBallRoster = (): boolean => ballRoster;
const setBallRoster = (v: boolean): void => {
  ballRoster = v;
  saveBool(BALL_ROSTER_KEY, v);
};

// 「自動照準（オートエイム）」設定（既定 OFF）。controls が参照し、ポケットへの経路が
// 通っている（隠れていない）球へ、現在の撞点/強さに合わせて自動でポケットIN角へ合わせる。
// プラクティスでも操作可（自由に的玉を動かせるため自動照準は有用）。対戦中のみ強制 OFF。
// 既定 OFF は F-04a「対戦中は通常 OFF を装備スキルで段階発動」と整合する初期方針。
const AUTO_AIM_KEY = 'pool.autoAim';
let autoAim = loadBool(AUTO_AIM_KEY, false);
/** 対戦中は強制 OFF（観戦/対戦は controls 側も二重ガード）。保存値は壊さない。 */
export const isAutoAim = (): boolean => autoAim && !matchActive;
const setAutoAim = (v: boolean): void => {
  if (matchActive) return; // 対戦中はトグル操作を無視（UI もグレーアウト）
  autoAim = v;
  saveBool(AUTO_AIM_KEY, v);
};

// 対戦/観戦中フラグ（main からの通知用）。プレイモード非依存の UI ロック判定で使う。
// 設定 UI は値そのものは保持し（破壊しない）、表示と操作可否のみを変える。
let matchActive = false;
/** 対戦/観戦中か（CPU戦・オンライン対戦・観戦のいずれか）。controls がスキルによる
 *  対戦中自動照準の発動条件判定に使う（isAutoAim は対戦中 false を返すため別途公開する）。 */
export const isMatchActive = (): boolean => matchActive;
/** UI 側の状態同期コールバック（createSettings 起動時に登録される）。 */
let onMatchActiveChange: (() => void) | null = null;
/** 対戦/観戦の開始・終了時に main が呼ぶ。設定パネル内のロック対象行を再同期。 */
export const setMatchActive = (v: boolean): void => {
  if (matchActive === v) return;
  matchActive = v;
  onMatchActiveChange?.();
};

// 「ショット入力方法」設定（既定 'pull'＝引いて撞く）。
// 'pull': 縦キューを下へ引いて離すとパワー比例で発射（タイミング要素なし）。**既定**。
//         キュートラックの上半分で仰角調整、下半分で引き量決定の二分割 UI を備える。
// 'timing': 撞点パッドの球をタップ→撞点確定＋パワーインジケーター（球上半弧）が
//           伸縮振動→再タップで決定して発射。タイミング撞きの代替方式。
const SHOT_INPUT_KEY = 'pool.shotInput';
type ShotInputMode = 'pull' | 'timing';
const loadShotInput = (): ShotInputMode => {
  try {
    const v = localStorage.getItem(SHOT_INPUT_KEY);
    // 明示的に 'timing' を保存していたユーザのみ timing を維持。
    // 未設定 or 旧 'pull' は新既定 'pull' を採用。
    return v === 'timing' ? 'timing' : 'pull';
  } catch {
    return 'pull';
  }
};
let shotInput: ShotInputMode = loadShotInput();
export const getShotInputMode = (): ShotInputMode => shotInput;
const setShotInputMode = (v: ShotInputMode): void => {
  shotInput = v;
  try {
    localStorage.setItem(SHOT_INPUT_KEY, v);
  } catch {
    // QuotaExceeded 等は無視
  }
  onShotInputChange?.();
};
/** controls がショット入力方式の切替時に UI を切り替えるためのコールバック。 */
let onShotInputChange: (() => void) | null = null;
/** controls.ts から登録（モード切替時に縦キュー表示/タイミング撞き UI を切り替える）。 */
export const subscribeShotInputChange = (cb: () => void): void => {
  onShotInputChange = cb;
};

// 「ブレイクのばらつき」設定（既定 ON）。実機ラックの個体差を模し、毎回のブレイクを変化させる。
// world のラックジッタ有効/無効に反映する（OFF＝完全再現可能）。
const BREAK_SPREAD_KEY = 'pool.breakSpread';
let breakSpread = loadBool(BREAK_SPREAD_KEY, true);
export const isBreakSpread = (): boolean => breakSpread;
const setBreakSpread = (v: boolean): void => {
  breakSpread = v;
  saveBool(BREAK_SPREAD_KEY, v);
  setRackSpread(v);
};
setRackSpread(breakSpread); // 起動時に現在値を world へ反映

// 「3ポイントルール（ブレイク）」設定（既定 OFF・9ボール）。main がルール判定に参照する。
// ON: 落球＋ヘッドストリング越えの合計が3以上で合法ブレイク。OFF: WPA既定（4球レール or 落球）。
const THREE_POINT_KEY = 'pool.threePointBreak';
let threePointBreak = loadBool(THREE_POINT_KEY, false);
/** 9ボール以外では強制 OFF（保存値は壊さない）。UI 上はグレーアウト。 */
export const isThreePointBreak = (): boolean =>
  threePointBreak && getPlayMode() === 'nineBall';
const setThreePointBreak = (v: boolean): void => {
  // 9ボール以外では操作を無視（UI はグレーアウトで操作不可だが二重ガード）。
  if (getPlayMode() !== 'nineBall') return;
  threePointBreak = v;
  saveBool(THREE_POINT_KEY, v);
};

// 「ショット力ガイド」設定は廃止（既定 OFF 固定）。
// 利用頻度が低かったため設定メニューから削除し、main 側のガイド描画ロジック自体は残置する
// （`isPowerGuide()` が常に false を返すので非表示になる）。
// STOPショット相当のタイミングは別途「キューきらめき」で表現する（main.ts）。
export const isPowerGuide = (): boolean => false;

// 「反射ガイド（軌跡ガイド）」設定（既定 OFF）。手球の予測軌跡（スピン/クッション/カーブ込み）の表示。
const REFLECT_GUIDE_KEY = 'pool.reflectGuide';
let reflectGuide = loadBool(REFLECT_GUIDE_KEY, false);
export const isReflectGuide = (): boolean => reflectGuide;
const setReflectGuide = (v: boolean): void => {
  reflectGuide = v;
  saveBool(REFLECT_GUIDE_KEY, v);
};

// 「ドラッグ位置ガイド」設定（既定 OFF）。構え中、canvas 上の
// 「ショット方向ドラッグ範囲」と「仰角/キュー手元ドラッグ範囲」を半透明の矢印＋
// 掴み幅（AIM_GRAB_PX=44 に対応する screen-space capsule）で可視化する。
// レール際で掴み範囲が狭くなる現象もそのまま見える＝学習価値が高い。
// 既定 OFF: 既存プレイヤーの視認体験を変えない／初心者は設定で ON にできる。
const DRAG_GUIDE_KEY = 'pool.dragGuide';
let dragGuide = loadBool(DRAG_GUIDE_KEY, false);
export const isDragGuideEnabled = (): boolean => dragGuide;
const setDragGuideEnabled = (v: boolean): void => {
  dragGuide = v;
  saveBool(DRAG_GUIDE_KEY, v);
};

// 「カット角ガイド」設定（既定 OFF）。狙っている的玉に対し、軸 u=手玉→的玉中心ライン と
// それに直交する n を辺方向とする**長方形（直角四辺形）**を表示する。対角頂点は
// 的玉中心 T とポケット狙点 P で、a=(P-T)·u / b=(P-T)·n の射影で 4 頂点を構築するため、
// 2 辺は常に u⊥n の直角関係（=正方形または長方形のみ・厳密に平行四辺形に縮退する角度はない）。
// |a|=|b| ⇔ カット角 45° ⇔ 正方形。厚いショット（θ小）ほど縦長・薄い（θ大）ほど横長になり、
// カット角度を直感的に目算できる。全モードで有効。
const CUT_GUIDE_KEY = 'pool.cutAngleGuide';
let cutAngleGuide = loadBool(CUT_GUIDE_KEY, false);
export const isCutAngleGuide = (): boolean => cutAngleGuide;
const setCutAngleGuide = (v: boolean): void => {
  cutAngleGuide = v;
  saveBool(CUT_GUIDE_KEY, v);
};

// 「テーブルコンディション（湿度）」設定（既定 0.5＝標準）。world.applyTableCondition に渡す。
const HUMIDITY_KEY = 'pool.humidity';
let humidity = loadNumber(HUMIDITY_KEY, 0.5);
export const getHumidity = (): number => humidity;
// 相手からのコンディション変更を反映するフック（createSettings がパネル更新付きで登録）。
let humidityExternalApply: ((h: number) => void) | null = null;
/** 外部（対戦相手）からの湿度変更を反映する。パネル表示も同期する。 */
export const applyExternalHumidity = (h: number): void => {
  if (!Number.isFinite(h)) return;
  if (humidityExternalApply) {
    humidityExternalApply(h);
  } else {
    humidity = Math.max(0, Math.min(1, h));
    saveNumber(HUMIDITY_KEY, humidity);
    applyTableCondition(humidity);
  }
};
// 湿度ラベル（スライダー位置→言葉）。
// 中央（=保存値 0.5）= 標準（従来の「湿潤」相当の重さ）。
// 0=軽い卓・1=非常に重い卓まで拡張。
const humidityLabel = (): string => {
  if (humidity < 0.15) return '乾燥（速い）';
  if (humidity < 0.4) return 'やや乾燥';
  if (humidity <= 0.6) return '標準';
  if (humidity < 0.85) return 'やや湿潤（重い）';
  return '湿潤（とても重い）';
};

// 「キューの飛び（スクワート）」設定。0〜10 段（既定 5）。撞点ズレによる手球の飛び量。
// Lv5 が従来の最大、係数 = level/5（Lv5→1.0 / Lv10→2.0）で 6〜10 はさらに強い。
const SQUIRT_KEY = 'pool.squirtLevel';
let squirtLevel = Math.max(0, Math.min(10, Math.round(loadNumber(SQUIRT_KEY, 5))));
/** 物理に渡す飛び係数（0〜2.0）。Lv5=1.0（従来最大）。 */
export const getSquirt = (): number => squirtLevel / 5;
const squirtLabel = (): string =>
  squirtLevel <= 0 ? 'なし' : `Lv ${squirtLevel} / 10`;

// ---- UI サイズ倍率（ユーザー設定・グローバル UI スケールに乗算） ----
// 自動スケール（controls.ts updateUiScale: 画面サイズ由来 0.65〜1.0）に乗算する体感調整倍率。
// 最終スケール = 自動スケール × 本倍率（mobile-hud-ux-guidelines.md §1.5）。
// 候補は固定リスト（1.0=標準〜1.30）。localStorage 永続・不正値は 1.0 へフォールバック。
const UI_USER_SCALE_KEY = 'pool.uiUserScale';
const UI_USER_SCALE_CHOICES: readonly number[] = [1.0, 1.05, 1.1, 1.15, 1.2, 1.25, 1.3];
let uiUserScale: number = ((): number => {
  try {
    const raw = localStorage.getItem(UI_USER_SCALE_KEY);
    if (raw === null) return 1.0;
    const v = Number(raw);
    return UI_USER_SCALE_CHOICES.includes(v) ? v : 1.0;
  } catch {
    return 1.0;
  }
})();
/** controls.ts updateUiScale が最終スケール算出時に乗算する。 */
export const getUiUserScale = (): number => uiUserScale;
const uiUserScaleLabel = (v: number): string =>
  v === 1.0 ? '1.0×（標準）' : `${v.toFixed(2)}×`;

// プレイモード（localStorage 永続・既定 9ボール）。main.ts が初期 world 構築に参照する。
const MODE_KEY = 'pool.playMode';
const MODES: { value: PlayMode; label: string }[] = [
  { value: 'practice', label: 'プラクティス' },
  { value: 'nineBall', label: '9ボール' },
  { value: 'tenBall', label: '10ボール' },
  { value: 'eightBall', label: '8ボール' },
];
let playMode: PlayMode = ((): PlayMode => {
  try {
    const v = localStorage.getItem(MODE_KEY) as PlayMode | null;
    if (v && MODES.some((m) => m.value === v)) return v;
  } catch {
    /* noop */
  }
  return 'nineBall';
})();
export const getPlayMode = (): PlayMode => playMode;
const modeLabel = (): string =>
  MODES.find((m) => m.value === playMode)?.label ?? '9ボール';

// 対戦チャット定型文（最大10件・既定は10件）。対戦中のチャットパネルが読み出す。
const CHAT_PRESETS_KEY = 'pool.chatPresets';
export const CHAT_PRESET_MAX = 10;
export const CHAT_TEXT_MAXLEN = 20;
/** 既定の定型文（未登録時に表示）。 */
const DEFAULT_CHAT_PRESETS: readonly string[] = [
  'おねがいします！',
  'ナイス！',
  'ラッキー！',
  'すみません！',
  'とんだ',
  'むずい',
  'ちかい',
  'まもります！',
  'またおねがいします！',
  'ありがとうございました！',
];
/** チャット本文の正規化（連続空白を1つに・前後除去・最大長カット）。 */
export const sanitizeChatText = (s: string): string =>
  s.replace(/\s+/g, ' ').trim().slice(0, CHAT_TEXT_MAXLEN);
const loadChatPresets = (): string[] => {
  try {
    const raw = localStorage.getItem(CHAT_PRESETS_KEY);
    if (raw === null) return DEFAULT_CHAT_PRESETS.slice();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_CHAT_PRESETS.slice();
    const out: string[] = [];
    for (const v of parsed) {
      if (typeof v !== 'string') continue;
      const t = sanitizeChatText(v);
      if (t) out.push(t);
      if (out.length >= CHAT_PRESET_MAX) break;
    }
    return out;
  } catch {
    return DEFAULT_CHAT_PRESETS.slice();
  }
};
let chatPresets = loadChatPresets();
/** 登録済みチャット定型文（コピーを返す・最大10件）。 */
export const getChatPresets = (): string[] => chatPresets.slice();
const saveChatPresets = (): void => {
  try {
    localStorage.setItem(CHAT_PRESETS_KEY, JSON.stringify(chatPresets));
  } catch (err) {
    console.error('chatPresets: localStorage 保存失敗:', err);
  }
};

interface Preset {
  label: string;
  hex: number;
}
const PRESETS: Preset[] = [
  { label: 'トーナメント緑', hex: 0x1d7a3e },
  { label: 'ブルー', hex: 0x2160a8 },
  { label: 'バーガンディ', hex: 0x8e2b2b },
  { label: 'ダークグリーン', hex: 0x14532b },
  { label: 'チャコール', hex: 0x3a4047 },
];

const toHex = (n: number): string => `#${(n & 0xffffff).toString(16).padStart(6, '0')}`;
const feltValueText = (): string => {
  const cur = getFeltColor();
  const p = PRESETS.find((x) => x.hex === cur);
  return p ? p.label : 'カスタム';
};

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
  parent: HTMLElement,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = cls;
  parent.appendChild(node);
  return node;
};

/** 行ラベルに「?」ヘルプボタンを付け、タップで説明文（行直下）を開閉する。 */
const attachHelp = (
  lab: HTMLElement,
  parent: HTMLElement,
  help: string,
  label: string,
): void => {
  const q = el('button', 'pool-row-help-btn', lab);
  q.textContent = '?';
  q.setAttribute('aria-label', `${label} の説明`);
  const desc = el('div', 'pool-row-help', parent); // 行の直後に挿入される
  desc.textContent = help;
  desc.hidden = true;
  q.addEventListener('click', (e) => {
    e.stopPropagation(); // ドリル行のページ遷移を抑止
    desc.hidden = !desc.hidden;
  });
};

/**
 * ON/OFF トグル行（浅い設定）。
 * - help: 「?」説明文（任意）。
 * - isDisabled: 行をグレーアウト＋操作不可にする条件（任意）。
 *   true のとき: トグルクリック無視・`.is-disabled` クラス付与で視覚的にロック表現。
 *   保存値は壊さない（get の現在値はそのまま表示する＝ロック解除時に復活）。
 */
const addToggleRow = (
  parent: HTMLElement,
  label: string,
  get: () => boolean,
  set: (v: boolean) => void,
  help?: string,
  isDisabled?: () => boolean,
): { sync: () => void } => {
  const row = el('div', 'pool-row', parent);
  const lab = el('span', 'pool-row-label', row);
  lab.textContent = label;
  if (help) attachHelp(lab, parent, help, label);
  const sw = el('button', 'pool-toggle', row);
  const sync = (): void => {
    const disabled = isDisabled?.() ?? false;
    sw.classList.toggle('is-on', get());
    row.classList.toggle('is-disabled', disabled);
    sw.disabled = disabled;
  };
  sw.addEventListener('click', () => {
    if (isDisabled?.()) return; // ロック中はクリックを完全に無視
    set(!get());
    sync();
  });
  sync();
  return { sync };
};

/** 現在値＋シェブロンのドリルダウン行（深い設定）。value 再表示用に sync を返す。
 *  isDisabled が true を返す間は `.is-disabled` を付与＋クリックを無視（保存値は破壊しない）。 */
const addDrillRow = (
  parent: HTMLElement,
  label: string,
  valueText: () => string,
  onOpen: () => void,
  help?: string,
  isDisabled?: () => boolean,
): { sync: () => void } => {
  const row = el('div', 'pool-row is-drill', parent);
  const lab = el('span', 'pool-row-label', row);
  lab.textContent = label;
  if (help) attachHelp(lab, parent, help, label);
  const val = el('span', 'pool-row-value', row);
  const chev = el('span', 'pool-row-chevron', row);
  chev.textContent = '›';
  const sync = (): void => {
    val.textContent = valueText();
    const disabled = isDisabled?.() ?? false;
    row.classList.toggle('is-disabled', disabled);
  };
  row.addEventListener('click', () => {
    if (isDisabled?.()) return; // ロック中はタップを完全に無視
    onOpen();
  });
  sync();
  return { sync };
};

// ---- PWA インストール行（addInstallRow） ----
// Android Chrome/Edge: beforeinstallprompt を保留しておき、本行のタップでネイティブの
//   「インストール」確認ダイアログを表示する（手順1ステップ）。
// iOS Safari: beforeinstallprompt 非対応のため、手順案内モーダルを表示する。
// その他（Firefox Android / Safari macOS 等）: 同モーダルで OS 別の汎用手順を案内する。
// すでに standalone（ホーム画面起動）の場合は本行を一切追加しない。

/**
 * Chromium 系で発火する `beforeinstallprompt` の最小型定義。
 * TS 標準型に含まれず、`any`/`unknown` 禁止規約に従い必要なメンバのみ宣言する。
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

/** index.html の早期スクリプトが保存する保留中プロンプトを取り出す。 */
const getDeferredInstallPrompt = (): BeforeInstallPromptEvent | null => {
  const w = window as Window & {
    __poolInstallPrompt?: BeforeInstallPromptEvent | null;
  };
  return w.__poolInstallPrompt ?? null;
};
/** 一度プロンプトを `prompt()` した後は再使用不可（仕様）。明示的にクリアする。 */
const clearDeferredInstallPrompt = (): void => {
  const w = window as Window & {
    __poolInstallPrompt?: BeforeInstallPromptEvent | null;
  };
  w.__poolInstallPrompt = null;
};

/** iOS / iPadOS の判定（iPadOS 13+ は Mac UA を返すため touch 対応で補完）。 */
const isIosLike = (): boolean => {
  try {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    if (ua.includes('Mac') && 'ontouchend' in document) return true;
    return false;
  } catch {
    return false;
  }
};

/** すでに standalone（ホーム画面 / アプリ）として起動済みか。 */
const isStandaloneApp = (): boolean => {
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    /* matchMedia 非対応は無視（古い環境のみ） */
  }
  // iOS Safari レガシー判定（`navigator.standalone` は TS 標準型に無いため型ガードで参照）。
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
};

/** インストール手順モーダルの DOM を遅延生成して返す（同一インスタンスを使い回し）。 */
const ensureInstallModal = (): HTMLDivElement => {
  const existing = document.getElementById('pool-pwa-install-modal');
  if (existing instanceof HTMLDivElement) return existing;
  const modal = document.createElement('div');
  modal.id = 'pool-pwa-install-modal';
  modal.className = 'pool-pwa-install-modal';
  modal.hidden = true;
  const card = document.createElement('div');
  card.className = 'pool-pwa-install-card';
  const title = document.createElement('div');
  title.className = 'pool-pwa-install-title';
  card.appendChild(title);
  const desc = document.createElement('div');
  desc.className = 'pool-pwa-install-desc';
  card.appendChild(desc);
  const ol = document.createElement('ol');
  ol.className = 'pool-pwa-install-steps';
  card.appendChild(ol);
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'pool-pwa-install-close';
  close.textContent = 'OK';
  close.addEventListener('click', () => {
    modal.hidden = true;
  });
  card.appendChild(close);
  modal.appendChild(card);
  // 背景タップで閉じる（誤操作復帰の常套）。
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.hidden = true;
  });
  document.body.appendChild(modal);
  return modal;
};

/** UA に応じて手順テキストを差し替えてモーダルを表示する。 */
const showInstallInstructionsModal = (): void => {
  const modal = ensureInstallModal();
  const titleEl = modal.querySelector(
    '.pool-pwa-install-title',
  ) as HTMLDivElement | null;
  const descEl = modal.querySelector(
    '.pool-pwa-install-desc',
  ) as HTMLDivElement | null;
  const stepsEl = modal.querySelector(
    '.pool-pwa-install-steps',
  ) as HTMLOListElement | null;
  if (!titleEl || !descEl || !stepsEl) {
    // 構築失敗時もモーダル自体は出して何らかのメッセージを見せる。
    modal.hidden = false;
    return;
  }
  let title: string;
  let desc: string;
  let steps: string[];
  if (isIosLike()) {
    title = '📱 ホーム画面に追加';
    desc = 'iPhone / iPad では以下の手順でホーム画面に追加できます:';
    steps = [
      '画面下の共有ボタン（□と↑）をタップ',
      'メニューから「ホーム画面に追加」を選択',
      '右上の「追加」をタップ',
    ];
  } else {
    // Android Firefox / デスクトップ Safari / その他: ブラウザ汎用手順。
    title = '📲 アプリとして追加';
    desc = 'お使いのブラウザのメニューから次の項目を選んでください:';
    steps = [
      'アドレスバー右の ⋮ または ⋯ メニューを開く',
      '「アプリをインストール」または「ホーム画面に追加」を選択',
      '確認ダイアログで「インストール」をタップ',
    ];
  }
  titleEl.textContent = title;
  descEl.textContent = desc;
  // 子要素を全置換（textContent='' は append された OL の li を消す）。
  stepsEl.textContent = '';
  for (const s of steps) {
    const li = document.createElement('li');
    li.textContent = s;
    stepsEl.appendChild(li);
  }
  modal.hidden = false;
};

/**
 * 設定リストに「📲 アプリとして追加」行を差し込む。
 * すでに standalone 起動なら何も追加しない。
 */
const addInstallRow = (parent: HTMLElement): void => {
  if (isStandaloneApp()) return;

  const row = el('div', 'pool-row is-drill', parent);
  const lab = el('span', 'pool-row-label', row);
  lab.textContent = '📲 アプリとして追加';
  const val = el('span', 'pool-row-value', row);
  const chev = el('span', 'pool-row-chevron', row);
  chev.textContent = '›';

  /** 現在の状態に応じて value 表示を更新する。 */
  const syncValueText = (): void => {
    if (isStandaloneApp()) {
      val.textContent = 'インストール済み';
      row.classList.add('is-disabled');
      return;
    }
    row.classList.remove('is-disabled');
    if (getDeferredInstallPrompt()) {
      val.textContent = 'インストール可能';
    } else if (isIosLike()) {
      val.textContent = '手順を見る';
    } else {
      val.textContent = '手順を見る';
    }
  };
  syncValueText();

  // beforeinstallprompt は遅延発火する（Chrome が条件を満たしてから数百 ms〜数秒）。
  // index.html の早期スクリプトが `pool:installable` / `pool:installed` を発行するので拾う。
  window.addEventListener('pool:installable', syncValueText);
  window.addEventListener('pool:installed', () => {
    row.hidden = true;
  });

  row.addEventListener('click', () => {
    if (row.classList.contains('is-disabled')) return;
    const deferred = getDeferredInstallPrompt();
    if (deferred) {
      // Android Chrome/Edge: ネイティブのインストール確認ダイアログを呼ぶ。
      deferred
        .prompt()
        .then(() => deferred.userChoice)
        .then((res) => {
          if (res.outcome === 'accepted') {
            row.hidden = true;
          }
          // 一度使った prompt は再利用不可（仕様）。クリア＋表示更新。
          clearDeferredInstallPrompt();
          syncValueText();
        })
        .catch((err) => {
          // ユーザーキャンセル等。次の機会まで何もしない。
          console.warn('[PWA] install prompt failed:', err);
        });
      return;
    }
    // それ以外（iOS / Firefox Android / Safari macOS 等）: 手順案内モーダル。
    showInstallInstructionsModal();
  });
};

export interface Settings {
  toggle: () => void;
}

export interface SettingsOpts {
  /** プレイモード変更時に main が world+views を再構築する。 */
  onPlayModeChange: (mode: PlayMode) => void;
  /** ラック調整を保存した時に main が現在モードを再ラックして反映する。 */
  onRackChange: () => void;
  /** テーブルコンディション（湿度）変更時。対戦中は main が相手へ通知する。 */
  onHumidityChange?: (humidity: number) => void;
  /** HUDレイアウト編集モードを開始する（設定パネルは閉じる）。 */
  onEditHud?: () => void;
  /** UI サイズ倍率の変更時。controls.ts が最終スケールを再計算して即時反映する。 */
  onUiScaleChange?: () => void;
  /** 設定パネル最下部の「アカウント」セクションを呼び出し側で組み立てる差込ポイント。
   *  呼び出し側で DOM を作って parent に append する。createSettings は親要素を提供するのみ。
   *  controls.ts が呼んで、ニックネーム表示・ログアウトボタン等を載せる。 */
  mountAccountSection?: (parent: HTMLElement) => void;
}

export const createSettings = (opts: SettingsOpts): Settings => {
  const panel = el('div', 'pool-settings', document.body);
  panel.hidden = true;

  const pages: HTMLElement[] = [];
  const showPage = (p: HTMLElement): void => {
    for (const x of pages) x.hidden = x !== p;
    // ラック調整ページのみパネルを広げて 3D エディタを大きく見せる。
    panel.classList.toggle('is-wide', p === rackPage);
  };
  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    if (open) showPage(mainPage);
    else rackEditor.hide(); // 閉じたら 3D 描画ループを停止
  };

  // ---- メインページ（設定リスト） ----
  const mainPage = el('div', 'pool-settings-page', panel);
  pages.push(mainPage);
  const mainHeader = el('div', 'pool-settings-header', mainPage);
  const mainTitle = el('span', 'pool-settings-title', mainHeader);
  mainTitle.textContent = '設定';
  const closeBtn = el('button', 'pool-settings-close', mainHeader);
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('click', () => setOpen(false));

  const list = el('div', 'pool-list', mainPage);

  // 対戦中（オンライン人間戦・CPU戦・観戦）はプレイモードを変更不可。
  // matchActive フラグで判定し、ドリル行をグレーアウトしてタップを無視する。
  // 対戦終了 (setMatchActive(false)) で自動的に解除される（onMatchActiveChange→modeRow.sync）。
  const modeRow = addDrillRow(
    list,
    'プレイモード',
    modeLabel,
    () => showPage(modePage),
    '9ボール / 10ボール / 8ボール / プラクティスを切り替えます。プラクティスは球を自由に追加・配置できます。対戦中（CPU戦・オンライン対戦・観戦中）はモード変更できません。',
    () => matchActive,
  );

  // ショット入力方法（pull=引いて撞く・timing=タイミング撞き）。横画面で縦キューが短くなる
  // 問題の代替手段としてタイミング撞きを用意。サブページで切り替える。
  const shotInputLabel = (): string =>
    getShotInputMode() === 'timing' ? 'タイミング撞き' : '引いて撞く';
  const shotInputRow = addDrillRow(
    list,
    'ショット入力方法',
    shotInputLabel,
    () => showPage(shotInputPage),
    '「引いて撞く」: 縦キューを下へ引いて離すとパワー比例で発射します（既定）。上半分は縦ドラッグまたは ∧/∨ ボタンでキュー仰角を調整できます。「タイミング撞き」: 撞点パッドの球をタップして撞点を決め、自動で伸縮するパワーインジケーター（球の上半円弧）が振れている間に、もう一度パッドの球をタップして発射します。横画面で縦キューが短く扱いにくい時に有効です。',
  );

  addToggleRow(
    list,
    '効果音',
    isSoundEnabled,
    setSoundEnabled,
    'ショット・球同士の衝突・クッション・ポケットインの効果音を鳴らします。',
  );

  // F-31 Broadcast カメラモード（既定 OFF）。
  // ON にすると Broadcast が一貫してカメラを管理し、autoTop/autoViewReset/手動 Top/狙撃カム F-03
  // が disabled になる（設計書 broadcast-camera-mode.md §1.3 の優先順位階層）。
  // 切替はサブページ（off / manual / director）。Director は P2 で実装（P1 では manual 相当）。
  let autoTopRow: { sync: () => void } | null = null;
  let shotFreezeCamRow: { sync: () => void } | null = null;
  let autoTopObscuredRow: { sync: () => void } | null = null;
  let viewResetMainRow: { sync: () => void } | null = null;
  const broadcastRow = addDrillRow(
    list,
    'Broadcast カメラモード',
    broadcastModeLabel,
    () => showPage(broadcastPage),
    '公式大会の放送カメラ技法（プレイヤー POV・真上俯瞰・ローレールシネマ・ビューティワイド）を 6 枠のプリセットで再現します。「手動切替」中はキーボード 1〜6 でプリセットを切り替えられます（Director は P2 で実装予定）。ON の間は「ショット後にトップビュー」「対象球が隠れたら自動トップ」「ビューアングル自動リセット」「狙撃カム」が Broadcast 側の管理となり個別操作はできなくなります。',
  );
  // Broadcast ON 中は競合する自動演出設定 4 行を disabled にする判定。
  const isBroadcastDisabled = (): boolean => isBroadcastOn();

  // ショット後トップビュー と 狙撃カム は排他（同時 ON 不可・両方 OFF は許容）。
  // 一方を ON にすると他方を自動 OFF にして対応行の表示を再同期する。
  // Broadcast ON 時は両方 disabled（Hot Head プリセットと F-03 統合が代替）。
  autoTopRow = addToggleRow(
    list,
    'ショット後にトップビュー',
    isAutoTopAfterShot,
    (v) => {
      setAutoTopAfterShot(v);
      if (v && isShotFreezeCam()) {
        setShotFreezeCam(false);
        shotFreezeCamRow?.sync();
      }
    },
    'ショット後、自動で真上からの視点に切り替えて球の動きを見やすくします。「狙撃カム（ポケットイン演出）」と排他で、ONにするともう一方は自動的にOFFになります（両方OFFは可）。Broadcast カメラモード ON 時は使用できません（Broadcast 側の Hot Head プリセットが代替します）。',
    isBroadcastDisabled,
  );
  // サブオプション: 構え時の的玉隠れに反応して真上ビューへ。
  // Broadcast ON 時は Broadcast 側の管理に統合（disabled）。
  autoTopObscuredRow = addToggleRow(
    list,
    '対象球が隠れたら自動トップビュー',
    isAutoTopOnObscured,
    setAutoTopOnObscured,
    '次に狙うべき的玉が他の球で完全に遮られて直接当てられない時、構えの段階で自動的に真上の視点に切り替えます。少しでも直接当てられる経路（カットショット含む）が残っていれば切り替えません。ショットを撞いた瞬間に解除され、静止後に再判定します。Broadcast カメラモード ON 時は使用できません。',
    isBroadcastDisabled,
  );

  // 狙撃カム（ポケットイン演出）。手玉・的玉・狙うポケットの3点が画面に収まっている時のみ発動。
  // ショット時のビュー（位置・向き・仰角・ズーム）を、的玉がポケットインするか・クッションに
  // 接触するか・明らかに外れた瞬間まで固定する。ポケットインの達成感を演出する目的。
  shotFreezeCamRow = addToggleRow(
    list,
    '狙撃カム（ポケットイン演出）',
    isShotFreezeCam,
    (v) => {
      setShotFreezeCam(v);
      if (v && isAutoTopAfterShot()) {
        setAutoTopAfterShot(false);
        autoTopRow?.sync();
      }
    },
    'ONにすると、手玉・的玉・狙うポケットがすべて画面に収まった状態でショットを撞いた時に、ショット時のカメラ（位置・向き・仰角・ズーム）を維持します。的玉がポケットに入った瞬間（または的玉がクッションに当たった瞬間・明らかに狙いから外れた瞬間）に通常のカメラ追従へスムーズに切り替わります。狙ったポケットへ入れる達成感を演出する設定です。「ショット後にトップビュー」と排他で、ONにするともう一方は自動的にOFFになります（両方OFFは可）。Broadcast カメラモード ON 時は使用できません（Broadcast 側に統合）。既定 OFF。',
    isBroadcastDisabled,
  );

  // ビューアングル自動リセット（既定 OFF / 秒数候補 0.5 秒）。orbit/zoom で視点を変えた後、
  // 最後の操作から N 秒で初期構図（手球後方・初期仰角）へ自動復帰する。
  // 既定では仰角・方位のみ復元（ズーム=半径は維持）。「ズームも含む」ONで半径も復元。
  // 「ズームも含む」と「秒数」は本トグルの**子オプション**として、本トグルが OFF のとき
  // グレーアウト＋タップ無視（独立した ON/OFF はあり得ない）。本トグル切替時に
  // setAutoViewReset を経由して子の sync() を呼ぶことで即座にロック表示が反映される。
  let viewResetZoomRow: { sync: () => void } | null = null;
  let viewResetSecRow: { sync: () => void } | null = null;
  // Broadcast ON 中も親トグル本体は disabled。子は親 OFF か Broadcast ON のどちらでも disabled。
  const isViewResetChildDisabled = (): boolean =>
    !isAutoViewReset() || isBroadcastOn();
  viewResetMainRow = addToggleRow(
    list,
    'ビューアングルを自動で戻す',
    isAutoViewReset,
    (v) => {
      setAutoViewReset(v);
      // 子オプションのロック状態を即座に同期。
      viewResetZoomRow?.sync();
      viewResetSecRow?.sync();
    },
    'ビューを回転・ズームした後、何も操作しないまま設定秒数が経過すると、自動で初期のカメラ構図（手球後方からの構え視点）に滑らかに戻します。既定では「向きと仰角」だけを戻し、ズーム（拡大率）は維持します。トップビュー（真上）中のピンチ/パンは対象外です。Broadcast カメラモード ON 時は使用できません（Broadcast 側のプリセットが優先されます）。',
    isBroadcastDisabled,
  );
  viewResetZoomRow = addToggleRow(
    list,
    '　ズームも含む',
    isAutoViewResetZoom,
    setAutoViewResetZoom,
    'ONにすると、自動リセット時にズーム（カメラ距離）も初期値に戻します。OFFのときは拡大したまま向きと仰角だけを戻すため、拡大した状態でショットを続けたい場合に便利です（親設定「ビューアングルを自動で戻す」が ON のときのみ有効）。',
    isViewResetChildDisabled,
  );
  const viewResetSecLabel = (): string => formatViewResetSec(getAutoViewResetSec());
  viewResetSecRow = addDrillRow(
    list,
    '　自動で戻すまでの秒数',
    viewResetSecLabel,
    () => showPage(viewResetSecPage),
    'ビューアングル自動リセットまでの待ち時間を選びます。短いほどすぐ元に戻りますが、視点を変えた状態でじっくり確認したい場合は長めに設定します（親設定「ビューアングルを自動で戻す」が ON のときのみ有効）。',
    isViewResetChildDisabled,
  );

  // ショット系UI「操作中の一時非表示」遅延 サブページ。
  // 方向アロー・ズーム・カメラオービット（ドラッグ/ピンチ）の操作中は撞点パッド・仰角・縦キュー・
  // 退避タブを隠し、操作完了から設定遅延後に復帰する。ポケット視認性・誤操作防止が目的。
  const shotUiTempHideLabel = (): string =>
    formatShotUiTempHide(getShotUiTempHideMs());
  const shotUiTempHideRow = addDrillRow(
    list,
    'ショット系UIの操作中自動非表示',
    shotUiTempHideLabel,
    () => showPage(shotUiTempHidePage),
    '方向アロー・ズーム・カメラオービット（ドラッグ/ピンチ）の操作中に、撞点パッド・仰角・縦キュー・退避タブを一時的に隠して、ポケット/球が UI に隠れたり、ショット力UIを誤って動かしたりするのを防ぎます。操作が終わってから設定した遅延（既定 800ms）後に元に戻ります。「OFF（隠さない）」を選ぶと従来通り常に表示します。',
  );

  addToggleRow(
    list,
    '残球表示',
    isBallRoster,
    setBallRoster,
    '画面左端に的玉を①〜縦に並べて表示します。ポケットインした球はアイコンが消えるため、どの球が落ちたか一目で分かります。アイコンをタッチするとその球へ照準を合わせます。',
  );

  addToggleRow(
    list,
    '方向アロー・ズームボタン',
    isControlUiVisible,
    setControlUiVisible,
    '画面下のショット方向アロー（◀◀ ◀ ▶ ▶▶）と、タイミング撞き中に撞点パッドの上に出るズーム⊕⊖ボタンの表示/非表示をまとめて切り替えます。OFFにすると画面がすっきりします。',
  );

  const autoAimRow = addToggleRow(
    list,
    '自動照準',
    isAutoAim,
    setAutoAim,
    'ポケットへの経路が通っている（隠れていない）球へ、現在の撞点・強さに合わせて自動でポケットINする角度にキューを合わせます（角度の制限はありません。補正時にキューが一瞬きらめきます）。ショット方向を手で動かすと解除され、的玉をタッチし直すと再び自動で合わせます。対戦中は使用できません（自動的にOFF）。',
    () => matchActive,
  );

  addToggleRow(
    list,
    'ブレイクのばらつき',
    isBreakSpread,
    setBreakSpread,
    'ラックにごく僅かな個体差（±0.2mm程度）を与え、同じブレイクでも毎回散らばりが変わるようにします（実機ラックの再現）。OFFにすると完全に同じ配置（再現可能）になります。設定変更は次のラックから反映されます。',
  );

  const threePointRow = addToggleRow(
    list,
    '3ポイントルール（ブレイク）',
    isThreePointBreak,
    setThreePointBreak,
    'ブレイクの合法条件を「落球数＋ヘッドストリング（キッチン線）を越えた的玉数の合計が3以上」に変更します（ポケットも1ポイント。例: 2球が線を越えれば落球1球で合法）。ソフトブレイク抑止のための一部大会ルール。OFFのときはWPA標準（4球以上レール到達 または 1球以上落球）です。9ボールのみ有効。',
    () => getPlayMode() !== 'nineBall',
  );

  // 軌跡ガイド（撞点・ショット力・仰角を反映した手玉の予測軌跡を可視化）。
  // ショット力ガイドは廃止し、STOPショット相当のタイミングはキューきらめきで表現する。
  addToggleRow(
    list,
    '軌跡ガイド',
    isReflectGuide,
    setReflectGuide,
    '撞点・ショット力・仰角を反映した手玉の予測軌跡（クッション反射・カーブ込み）を表示します。',
  );

  // カット角ガイド（イメージ長方形でカット角度を視覚化）。
  addToggleRow(
    list,
    'カット角ガイド',
    isCutAngleGuide,
    setCutAngleGuide,
    '狙っている的玉とポケット狙点を対角とする長方形（直角四辺形）を表示します。1 辺の方向は手玉→的玉ライン、もう 1 辺はそれと直角。カット角 45°で正方形になり、厚いショット（角度が小さい）ほど縦長・薄い（角度が大きい）ほど横長になるため、カット角度を直感的に目算できます。',
  );

  // ドラッグ位置ガイド（盤面上のドラッグ操作範囲を可視化）。
  // canvas 上の「ショット方向ドラッグ範囲（手玉→前方）」と「仰角/キュー手元ドラッグ範囲（手玉→キュー手元）」を
  // 半透明の矢印＋掴み幅 capsule（CSS 44px = controls.ts AIM_GRAB_PX と同期）で可視化する。
  // レール際でキューが立つことにより掴み範囲が screen-space で狭くなる現象もそのまま見える。
  addToggleRow(
    list,
    'ドラッグ位置ガイド',
    isDragGuideEnabled,
    setDragGuideEnabled,
    '構え中、画面上で「キューの向き（ショット方向）」と「仰角（キュー手元）」をドラッグで変更できる範囲を、薄い矢印と帯で可視化します。範囲外をドラッグすると視点回転になります。レール際でキューが立ち上がる時は掴み範囲が狭くなり、その様子も帯の長さで分かります。',
  );

  const feltRow = addDrillRow(
    list,
    'ラシャの色',
    feltValueText,
    () => showPage(feltPage),
    'テーブル表面（ラシャ）の色を変更します。',
  );

  const condRow = addDrillRow(
    list,
    'テーブルコンディション',
    humidityLabel,
    () => showPage(condPage),
    'クロスとクッションの摩擦（湿度）を調整します。中央が「標準」、右にいくほど球の転がりが重く、左にいくほど軽く（速く）なります。反発の強さ自体は変わりません。最大では非常に重い卓になり、繊細なタッチが要求されます。',
  );

  const squirtRow = addDrillRow(
    list,
    'キューの飛び',
    squirtLabel,
    () => showPage(squirtPage),
    '撞点を横にずらした時の手玉の飛び（スクワート）量。キューの個体差を表します。横撞点で english と逆へ撞き出し、低速ではスワーブで english 側へ戻ります（ショットの強さで変化）。',
  );

  const rackRow = addDrillRow(
    list,
    'ラック調整',
    () => (playMode === 'practice' ? '—' : modeLabel()),
    () => {
      showPage(rackPage);
      rackEditor.show();
    },
    '各球の位置を微調整してラックの組み方を保存します。外周が密着するほどよく割れ、中央付近にわざと隙間を作ると戦術になります（起動時・リセット時に反映）。',
    // プラクティスはブレイクラックを使わず球を自由配置するため、ラック調整は対象外＝無効化（グレーアウト＋タップ無視）。
    () => playMode === 'practice',
  );

  const chatRow = addDrillRow(
    list,
    '対戦チャット定型文',
    () => `${chatPresets.length} / ${CHAT_PRESET_MAX}件`,
    () => showPage(chatPage),
    '対戦中に相手へ送れる定型文（最大5件）を登録します。対戦中はワンタップで送信できます（自由入力も可能）。',
  );

  // チャット読み上げ（TTS）設定。性別 + プリセットを選び、プレビュー再生可能。
  // 自分の音声プロファイルは送信メッセージに同梱され、受信側はそれで読み上げる（＝相手は相手の設定に従う）。
  // 非対応ブラウザでは行ごとグレーアウト。
  const ttsRowValueText = (): string => {
    if (!chatTts.isSupported()) return '非対応';
    if (!chatTts.isEnabled()) return 'OFF';
    const id = chatTts.getMyPresetId();
    const presetLabel =
      id === 'custom'
        ? CUSTOM_PRESET_LABEL
        : chatTts.getPresets().find((p) => p.id === id)?.label ?? '標準';
    const genderLabel = chatTts.getMyGender() === 'male' ? '男性' : '女性';
    return `${genderLabel} / ${presetLabel}`;
  };
  const ttsRow = addDrillRow(
    list,
    'チャット読み上げ',
    ttsRowValueText,
    () => showPage(ttsPage),
    'チャットメッセージを音声で読み上げます（自分の声は性別とプリセットで設定可能）。送信メッセージには自分の音声プロファイルが同梱され、相手は相手の設定で読み上げます。',
    () => !chatTts.isSupported(),
  );

  // UI サイズ倍率 — グローバル UI スケール（自動）に乗算する体感調整。
  // サブページで選択すると即時反映（パネルは開いたまま＝大きさを見比べながら選べる）。
  const uiScaleRow = addDrillRow(
    list,
    'UIサイズ倍率',
    () => uiUserScaleLabel(uiUserScale),
    () => showPage(uiScalePage),
    'ボタンや操作 UI 全体の大きさを調整します。画面サイズに応じた自動調整に、この倍率を掛けた大きさで表示されます（リロード後も維持）。',
  );

  // HUDレイアウト編集 — 設定を閉じてドラッグ移動モードへ入る。
  // 編集中は画面上部に「完了 / リセット」ツールバーが現れ、各HUDを自由位置へドラッグできる。
  addDrillRow(
    list,
    'HUDレイアウト編集',
    () => '配置を変更',
    () => {
      setOpen(false); // 設定パネルを閉じてから編集モードへ
      opts.onEditHud?.();
    },
    '画面の表示要素（カット角・残球・ロビー・撞点UI・キュー）をドラッグで自由な位置へ移動できます。ポケットと重なって見にくい時に調整してください。「完了」で確定、「リセット」で初期位置に戻ります。',
  );

  // PWA: 「📲 アプリとして追加」（standalone 起動時は本行を追加しない）。
  // 詳細は addInstallRow / showInstallModal の実装コメント参照。
  addInstallRow(list);

  // ---- 設定の初期化（リセット） ----
  // 「基本リセット」: ON/OFF 系トグル設定の全項目を初期値へ戻す（必ず実行）。
  // 「オプションリセット」: チェックボックスで選んだ項目のみ削除（デフォルトは全 OFF=消去しない）。
  // ニックネーム・認証・成績・インベントリ・ドリル進捗は完全除外（リセット対象外・選択肢にも出さない）。
  // 実行後は localStorage の状態を確実にメモリへ反映するため location.reload() で再起動する。
  addDrillRow(
    list,
    '⚠ 設定の初期化',
    () => 'リセット',
    () => showPage(resetPage),
    'ON/OFF設定を初期値に戻します。プレイモードやテーブルコンディションなどの個別カスタム値や、ラック調整・ブレイク手玉位置・HUDレイアウトなどはチェックを入れた項目だけ消去します。ニックネーム・成績・インベントリ・ドリル進捗は対象外です。実行後はページが再読み込みされます。',
  );

  addDrillRow(
    list,
    'ライセンス情報',
    () => 'GPL-3.0',
    () => showPage(licensePage),
  );

  // ---- アカウント情報セクション（設定パネル最下部・呼び出し側で組み立て） ----
  // 呼び出し側 (controls.ts) が parent に DOM を append する。
  // 中身: ニックネーム表示・ログアウトボタン等。サーバー認証していない場合は何も表示しない選択も可。
  if (opts.mountAccountSection) {
    const accountSlot = el('div', 'pool-settings-account', mainPage);
    opts.mountAccountSection(accountSlot);
  }

  // ---- Broadcast カメラモード サブページ（ラジオ選択） ----
  // off / manual / director の 3 択。選択後はメインページへ戻り、競合する自動演出設定 4 行を
  // 即座に disabled / 通常状態へ同期する。
  const broadcastPage = el('div', 'pool-settings-page', panel);
  pages.push(broadcastPage);
  broadcastPage.hidden = true;
  const broadcastHeader = el('div', 'pool-settings-header', broadcastPage);
  const broadcastBack = el('button', 'pool-settings-back', broadcastHeader);
  broadcastBack.textContent = '‹ 戻る';
  broadcastBack.addEventListener('click', () => {
    broadcastRow.sync();
    showPage(mainPage);
  });
  const broadcastTitle = el('span', 'pool-settings-title', broadcastHeader);
  broadcastTitle.textContent = 'Broadcast カメラモード';
  const broadcastList = el('div', 'pool-list', broadcastPage);
  const broadcastChoices: { value: BroadcastModeValue; label: string; help: string }[] = [
    {
      value: 'off',
      label: 'OFF（既存挙動）',
      help: '従来通り「ショット後トップビュー」「狙撃カム」など個別設定で制御します。',
    },
    {
      value: 'manual',
      label: '手動切替（キー 1〜6）',
      help: 'キーボード 1〜6 でプリセット（Player POV / Hot Head / Low-Rail / Beauty Wide 等）を切替できます。Director は P2 で実装予定。',
    },
    {
      value: 'director',
      label: 'Director（自動カット・P2 で本実装）',
      help: '将来 Director 状態機械が盤面状況で自動カットします。P1 では手動切替と同じ動作になります。',
    },
  ];
  const broadcastChecks: { row: HTMLElement; value: BroadcastModeValue }[] = [];
  const syncBroadcastChecks = (): void => {
    for (const c of broadcastChecks) {
      c.row.classList.toggle('is-selected', c.value === broadcastMode);
    }
  };
  for (const c of broadcastChoices) {
    const row = el('div', 'pool-row is-drill', broadcastList);
    const lab = el('span', 'pool-row-label', row);
    lab.textContent = c.label;
    attachHelp(lab, broadcastList, c.help, c.label);
    const check = el('span', 'pool-row-check', row);
    check.textContent = '✓';
    row.addEventListener('click', () => {
      const prev = broadcastMode;
      setBroadcastMode(c.value);
      syncBroadcastChecks();
      broadcastRow.sync();
      // Broadcast 状態が変化したら、競合する 4 行（autoTop/autoTopObscured/viewReset/freezeCam）の
      // disabled 表示と sync を再評価。プログラム的に値は変更しないため保存値は保持される。
      if (prev !== c.value) {
        autoTopRow?.sync();
        autoTopObscuredRow?.sync();
        shotFreezeCamRow?.sync();
        viewResetMainRow?.sync();
        viewResetZoomRow?.sync();
        viewResetSecRow?.sync();
        onBroadcastModeChange?.();
      }
      showPage(mainPage);
    });
    broadcastChecks.push({ row, value: c.value });
  }
  syncBroadcastChecks();

  // F-31 P2b §12.7: 観戦中は Director を既定 ON にするか（永続保存）。
  // 観戦突入時に保存値が OFF なら一時的に Director を適用、観戦終了で復帰する。
  // 既定 ON（観戦体験を放送品質に揃える）。
  addToggleRow(
    broadcastList,
    '観戦中は Director を既定 ON',
    isBroadcastSpectatorDefault,
    setBroadcastSpectatorDefault,
    '観戦に入った時、Broadcast カメラモードが OFF だった場合、一時的に Director（自動カット）を適用します。観戦を抜けると元の保存値（OFF）へ戻ります。Manual / Director を明示保存している場合は本設定の影響を受けません（保存値の挙動を尊重）。',
  );

  // F-31 P3.6 §14.16: カメラ切替 UI（HUD ピルバー）の表示 ON/OFF（既定 OFF）。
  // 観戦中は本設定に関わらず強制表示。
  addToggleRow(
    broadcastList,
    'カメラ切替 UI を表示',
    isBroadcastBarVisible,
    setBroadcastBarVisible,
    'Broadcast カメラモード ON のときに、上中央のカメラ切替ピルバー（POV/BM/LOW/JIB/POCK/WIDE）を表示します。既定 OFF（プレイ中の視覚ノイズを抑える）。観戦中は本設定に関わらず自動的に表示されます（現在のカメラを視覚で把握するため）。',
  );

  // ---- プレイモード サブページ（ラジオ選択） ----
  const modePage = el('div', 'pool-settings-page', panel);
  pages.push(modePage);
  modePage.hidden = true;
  const modeHeader = el('div', 'pool-settings-header', modePage);
  const modeBack = el('button', 'pool-settings-back', modeHeader);
  modeBack.textContent = '‹ 戻る';
  modeBack.addEventListener('click', () => {
    modeRow.sync();
    showPage(mainPage);
  });
  const modeTitle = el('span', 'pool-settings-title', modeHeader);
  modeTitle.textContent = 'プレイモード';
  const modeList = el('div', 'pool-list', modePage);
  const modeChecks: { row: HTMLElement; value: PlayMode }[] = [];
  const syncModeChecks = (): void => {
    for (const m of modeChecks) {
      m.row.classList.toggle('is-selected', m.value === playMode);
    }
  };
  // 10ボール / 8ボールはルール未実装のため、選択リスト上で「（工事中）」を併記する。
  // 内部値（PlayMode）には触れず、サブページの表示ラベルのみ変更する。
  const isUnderConstruction = (v: PlayMode): boolean =>
    v === 'tenBall' || v === 'eightBall';
  for (const m of MODES) {
    const row = el('div', 'pool-row is-drill', modeList);
    const lab = el('span', 'pool-row-label', row);
    lab.textContent = isUnderConstruction(m.value)
      ? `${m.label}（工事中）`
      : m.label;
    const check = el('span', 'pool-row-check', row);
    check.textContent = '✓';
    row.addEventListener('click', () => {
      // 対戦中はサブページが既に開いていてもモード切替を防ぐ二重ガード。
      // 通常 modeRow.isDisabled でサブページに入れないが、開いた直後に対戦開始した
      // ケースでも安全に弾く。
      if (matchActive) return;
      playMode = m.value;
      try {
        localStorage.setItem(MODE_KEY, playMode);
      } catch (err) {
        console.error('playMode: localStorage 保存失敗:', err);
      }
      syncModeChecks();
      modeRow.sync();
      rackRow.sync(); // ラック調整行の表示モードも更新（モード別ラックのため）
      autoAimRow.sync(); // プラクティスでは自動照準が自動OFF＋グレーアウトになるため同期
      threePointRow.sync(); // 9ボール以外ではグレーアウトになるため同期
      opts.onPlayModeChange(playMode);
      showPage(mainPage);
    });
    modeChecks.push({ row, value: m.value });
  }
  syncModeChecks();

  // ---- UI サイズ倍率 サブページ（ラジオ選択・即時反映） ----
  // 選択してもメインページへ戻らず、その場で HUD の大きさを見比べながら選べる。
  const uiScalePage = el('div', 'pool-settings-page', panel);
  pages.push(uiScalePage);
  uiScalePage.hidden = true;
  const uiScaleHeader = el('div', 'pool-settings-header', uiScalePage);
  const uiScaleBack = el('button', 'pool-settings-back', uiScaleHeader);
  uiScaleBack.textContent = '‹ 戻る';
  uiScaleBack.addEventListener('click', () => {
    uiScaleRow.sync();
    showPage(mainPage);
  });
  const uiScaleTitle = el('span', 'pool-settings-title', uiScaleHeader);
  uiScaleTitle.textContent = 'UIサイズ倍率';
  const uiScaleList = el('div', 'pool-list', uiScalePage);
  const uiScaleChecks: { row: HTMLElement; value: number }[] = [];
  const syncUiScaleChecks = (): void => {
    for (const c of uiScaleChecks) {
      c.row.classList.toggle('is-selected', c.value === uiUserScale);
    }
  };
  for (const v of UI_USER_SCALE_CHOICES) {
    const row = el('div', 'pool-row is-drill', uiScaleList);
    const lab = el('span', 'pool-row-label', row);
    lab.textContent = uiUserScaleLabel(v);
    const check = el('span', 'pool-row-check', row);
    check.textContent = '✓';
    row.addEventListener('click', () => {
      uiUserScale = v;
      try {
        localStorage.setItem(UI_USER_SCALE_KEY, String(v));
      } catch (err) {
        console.error('uiUserScale: localStorage 保存失敗:', err);
      }
      syncUiScaleChecks();
      uiScaleRow.sync();
      // 即時反映（controls.ts が --pool-ui-scale を再計算しトップバー圧縮も再判定）。
      opts.onUiScaleChange?.();
    });
    uiScaleChecks.push({ row, value: v });
  }
  syncUiScaleChecks();

  // ---- ショット入力方法 サブページ（ラジオ選択） ----
  const shotInputPage = el('div', 'pool-settings-page', panel);
  pages.push(shotInputPage);
  shotInputPage.hidden = true;
  const shotInputHeader = el('div', 'pool-settings-header', shotInputPage);
  const shotInputBack = el('button', 'pool-settings-back', shotInputHeader);
  shotInputBack.textContent = '‹ 戻る';
  shotInputBack.addEventListener('click', () => {
    shotInputRow.sync();
    showPage(mainPage);
  });
  const shotInputTitle = el('span', 'pool-settings-title', shotInputHeader);
  shotInputTitle.textContent = 'ショット入力方法';
  const shotInputList = el('div', 'pool-list', shotInputPage);
  const SHOT_INPUT_OPTIONS: Array<{ value: ShotInputMode; label: string; help: string }> = [
    {
      value: 'pull',
      label: '引いて撞く（既定）',
      help: '撞点パッドの上にある縦キューを下へ引き下げて離すと、引き量に比例したパワーで発射します。タイミング要素はなく、引き量だけで強さを決めます。キュートラックの上半分で仰角調整、下半分で引き量決定の二分割 UI が利用できます。',
    },
    {
      value: 'timing',
      label: 'タイミング撞き',
      help: '撞点パッドの球をタップして撞点を決めると、球の上半円に沿ってパワーインジケーターが伸縮（最大で約1秒停止）し始めます。狙ったタイミングでもう一度球の中をタップすると、その瞬間のパワーで発射します。縦キューは非表示になります。',
    },
  ];
  const shotInputChecks: { row: HTMLElement; value: ShotInputMode }[] = [];
  const syncShotInputChecks = (): void => {
    for (const s of shotInputChecks) {
      s.row.classList.toggle('is-selected', s.value === getShotInputMode());
    }
  };
  for (const opt of SHOT_INPUT_OPTIONS) {
    const row = el('div', 'pool-row is-drill', shotInputList);
    const lab = el('span', 'pool-row-label', row);
    lab.textContent = opt.label;
    attachHelp(lab, shotInputList, opt.help, opt.label);
    const check = el('span', 'pool-row-check', row);
    check.textContent = '✓';
    row.addEventListener('click', () => {
      setShotInputMode(opt.value);
      syncShotInputChecks();
      shotInputRow.sync();
      showPage(mainPage);
    });
    shotInputChecks.push({ row, value: opt.value });
  }
  // 3D 統合ショットUI（撞点＋仰角＋発射を 1 つの 3D ウィジェットに統合する新方式）。
  // 実装途中のため「工事中」として選択不可（グレーアウト）で予告のみ表示する。
  // クリックしても何もしない＝保存値（pull/timing）を壊さない。
  {
    const wipRow = el('div', 'pool-row is-drill is-disabled', shotInputList);
    const wipLab = el('span', 'pool-row-label', wipRow);
    wipLab.textContent = '3D ショットUI（工事中）';
    attachHelp(
      wipLab,
      shotInputList,
      '撞点・仰角・発射を 1 つの 3D ウィジェットに統合する新しい入力方式を開発中です。現在は選択できません。',
      '3D ショットUI（工事中）',
    );
  }
  syncShotInputChecks();

  // ---- ビューアングル自動リセット 秒数 サブページ（ラジオ選択） ----
  const viewResetSecPage = el('div', 'pool-settings-page', panel);
  pages.push(viewResetSecPage);
  viewResetSecPage.hidden = true;
  const viewResetSecHeader = el('div', 'pool-settings-header', viewResetSecPage);
  const viewResetSecBack = el('button', 'pool-settings-back', viewResetSecHeader);
  viewResetSecBack.textContent = '‹ 戻る';
  viewResetSecBack.addEventListener('click', () => {
    viewResetSecRow.sync();
    showPage(mainPage);
  });
  const viewResetSecTitle = el('span', 'pool-settings-title', viewResetSecHeader);
  viewResetSecTitle.textContent = '自動で戻すまでの秒数';
  const viewResetSecList = el('div', 'pool-list', viewResetSecPage);
  const viewResetSecChecks: { row: HTMLElement; value: number }[] = [];
  const syncViewResetSecChecks = (): void => {
    for (const s of viewResetSecChecks) {
      s.row.classList.toggle('is-selected', s.value === getAutoViewResetSec());
    }
  };
  for (const sec of AUTO_VIEW_RESET_SEC_CHOICES) {
    const row = el('div', 'pool-row is-drill', viewResetSecList);
    const lab = el('span', 'pool-row-label', row);
    lab.textContent = formatViewResetSec(sec);
    const check = el('span', 'pool-row-check', row);
    check.textContent = '✓';
    row.addEventListener('click', () => {
      setAutoViewResetSec(sec);
      syncViewResetSecChecks();
      viewResetSecRow.sync();
      showPage(mainPage);
    });
    viewResetSecChecks.push({ row, value: sec });
  }
  syncViewResetSecChecks();

  // ---- ショット系UI 操作中自動非表示 サブページ（ラジオ選択） ----
  // 候補: OFF(0) / 250 / 500 / 800 / 1200 ms。
  // 値変更時は親行の表示ラベルを sync() で更新してからメインへ戻る（viewResetSec と同様）。
  const shotUiTempHidePage = el('div', 'pool-settings-page', panel);
  pages.push(shotUiTempHidePage);
  shotUiTempHidePage.hidden = true;
  const shotUiTempHideHeader = el('div', 'pool-settings-header', shotUiTempHidePage);
  const shotUiTempHideBack = el('button', 'pool-settings-back', shotUiTempHideHeader);
  shotUiTempHideBack.textContent = '‹ 戻る';
  shotUiTempHideBack.addEventListener('click', () => {
    shotUiTempHideRow.sync();
    showPage(mainPage);
  });
  const shotUiTempHideTitle = el('span', 'pool-settings-title', shotUiTempHideHeader);
  shotUiTempHideTitle.textContent = 'ショット系UIの自動非表示';
  const shotUiTempHideList = el('div', 'pool-list', shotUiTempHidePage);
  const shotUiTempHideChecks: { row: HTMLElement; value: number }[] = [];
  const syncShotUiTempHideChecks = (): void => {
    for (const s of shotUiTempHideChecks) {
      s.row.classList.toggle('is-selected', s.value === getShotUiTempHideMs());
    }
  };
  for (const ms of SHOT_UI_TEMP_HIDE_CHOICES) {
    const row = el('div', 'pool-row is-drill', shotUiTempHideList);
    const lab = el('span', 'pool-row-label', row);
    lab.textContent = formatShotUiTempHide(ms);
    const check = el('span', 'pool-row-check', row);
    check.textContent = '✓';
    row.addEventListener('click', () => {
      setShotUiTempHideMs(ms);
      syncShotUiTempHideChecks();
      shotUiTempHideRow.sync();
      showPage(mainPage);
    });
    shotUiTempHideChecks.push({ row, value: ms });
  }
  syncShotUiTempHideChecks();

  // ---- ラシャ色サブページ ----
  const feltPage = el('div', 'pool-settings-page', panel);
  pages.push(feltPage);
  feltPage.hidden = true;
  const feltHeader = el('div', 'pool-settings-header', feltPage);
  const backBtn = el('button', 'pool-settings-back', feltHeader);
  backBtn.textContent = '‹ 戻る';
  backBtn.addEventListener('click', () => {
    feltRow.sync();
    showPage(mainPage);
  });
  const feltTitle = el('span', 'pool-settings-title', feltHeader);
  feltTitle.textContent = 'ラシャの色';

  const swatchRow = el('div', 'pool-settings-swatches', feltPage);
  const swatches: { btn: HTMLButtonElement; hex: number }[] = [];
  const picker = el('input', 'pool-settings-picker', feltPage);
  picker.type = 'color';

  const markActive = (): void => {
    const cur = getFeltColor();
    for (const s of swatches) s.btn.classList.toggle('is-active', s.hex === cur);
    picker.value = toHex(cur);
  };
  const apply = (hex: number): void => {
    setFeltColor(hex);
    markActive();
  };
  for (const p of PRESETS) {
    const btn = el('button', 'pool-settings-swatch', swatchRow);
    btn.style.background = toHex(p.hex);
    btn.title = p.label;
    btn.addEventListener('click', () => apply(p.hex));
    swatches.push({ btn, hex: p.hex });
  }
  picker.addEventListener('input', () => {
    const n = parseInt(picker.value.replace('#', ''), 16);
    if (Number.isFinite(n)) apply(n);
  });
  markActive();

  // ---- テーブルコンディション サブページ（湿度スライダー） ----
  const condPage = el('div', 'pool-settings-page', panel);
  pages.push(condPage);
  condPage.hidden = true;
  const condHeader = el('div', 'pool-settings-header', condPage);
  const condBack = el('button', 'pool-settings-back', condHeader);
  condBack.textContent = '‹ 戻る';
  condBack.addEventListener('click', () => {
    condRow.sync();
    showPage(mainPage);
  });
  const condTitle = el('span', 'pool-settings-title', condHeader);
  condTitle.textContent = 'テーブルコンディション';

  const condVal = el('div', 'pool-settings-cond-val', condPage);
  const slider = el('input', 'pool-settings-slider', condPage);
  slider.type = 'range';
  slider.min = '0';
  slider.max = '1';
  slider.step = '0.01';
  slider.value = String(humidity);
  const condEnds = el('div', 'pool-settings-cond-ends', condPage);
  const condDry = el('span', 'pool-settings-cond-end', condEnds);
  condDry.textContent = '乾燥（速い）';
  const condWet = el('span', 'pool-settings-cond-end', condEnds);
  condWet.textContent = '湿潤（重い）';
  const syncCond = (): void => {
    condVal.textContent = `湿度: ${humidityLabel()}`;
  };
  syncCond();
  slider.addEventListener('input', () => {
    const n = parseFloat(slider.value);
    if (!Number.isFinite(n)) return;
    humidity = n;
    saveNumber(HUMIDITY_KEY, humidity);
    syncCond();
    applyTableCondition(humidity);
    opts.onHumidityChange?.(humidity); // 対戦中は相手へ通知（main 経由）
  });
  // 相手からの変更を反映する経路（スライダー位置・ラベル・物理も同期）。
  humidityExternalApply = (h: number): void => {
    humidity = Math.max(0, Math.min(1, h));
    slider.value = String(humidity);
    saveNumber(HUMIDITY_KEY, humidity);
    syncCond();
    applyTableCondition(humidity);
  };

  // ---- キューの飛び（スクワート）サブページ ----
  const squirtPage = el('div', 'pool-settings-page', panel);
  pages.push(squirtPage);
  squirtPage.hidden = true;
  const squirtHeader = el('div', 'pool-settings-header', squirtPage);
  const squirtBack = el('button', 'pool-settings-back', squirtHeader);
  squirtBack.textContent = '‹ 戻る';
  squirtBack.addEventListener('click', () => {
    squirtRow.sync();
    showPage(mainPage);
  });
  const squirtTitle = el('span', 'pool-settings-title', squirtHeader);
  squirtTitle.textContent = 'キューの飛び';

  const squirtVal = el('div', 'pool-settings-cond-val', squirtPage);
  const squirtSlider = el('input', 'pool-settings-slider', squirtPage);
  squirtSlider.type = 'range';
  squirtSlider.min = '0';
  squirtSlider.max = '10';
  squirtSlider.step = '1';
  squirtSlider.value = String(squirtLevel);
  const squirtEnds = el('div', 'pool-settings-cond-ends', squirtPage);
  const squirtLow = el('span', 'pool-settings-cond-end', squirtEnds);
  squirtLow.textContent = 'なし';
  const squirtHigh = el('span', 'pool-settings-cond-end', squirtEnds);
  squirtHigh.textContent = '最大(Lv10)';
  const syncSquirt = (): void => {
    squirtVal.textContent = `飛び: ${squirtLabel()}`;
  };
  syncSquirt();
  squirtSlider.addEventListener('input', () => {
    const n = parseInt(squirtSlider.value, 10);
    if (!Number.isFinite(n)) return;
    squirtLevel = Math.max(0, Math.min(10, n));
    saveNumber(SQUIRT_KEY, squirtLevel);
    syncSquirt();
  });

  // ---- ラック調整 サブページ ----
  const rackPage = el('div', 'pool-settings-page', panel);
  pages.push(rackPage);
  rackPage.hidden = true;
  const rackHeader = el('div', 'pool-settings-header', rackPage);
  const rackBack = el('button', 'pool-settings-back', rackHeader);
  rackBack.textContent = '‹ 戻る';
  rackBack.addEventListener('click', () => {
    rackEditor.hide();
    rackRow.sync();
    showPage(mainPage);
  });
  const rackTitle = el('span', 'pool-settings-title', rackHeader);
  rackTitle.textContent = 'ラック調整';
  const rackEditor = createRackEditor({
    getMode: getPlayMode,
    onChange: opts.onRackChange,
  });
  rackPage.appendChild(rackEditor.element);

  // ---- 対戦チャット定型文 サブページ（最大5件の登録・削除） ----
  const chatPage = el('div', 'pool-settings-page', panel);
  pages.push(chatPage);
  chatPage.hidden = true;
  const chatHeader = el('div', 'pool-settings-header', chatPage);
  const chatBack = el('button', 'pool-settings-back', chatHeader);
  chatBack.textContent = '‹ 戻る';
  chatBack.addEventListener('click', () => {
    chatRow.sync();
    showPage(mainPage);
  });
  const chatTitle = el('span', 'pool-settings-title', chatHeader);
  chatTitle.textContent = '対戦チャット定型文';
  const chatEditList = el('div', 'pool-list', chatPage);
  const chatAddWrap = el('div', 'pool-chat-add', chatPage);
  const chatAddInput = el('input', 'pool-chat-add-input', chatAddWrap);
  chatAddInput.type = 'text';
  chatAddInput.maxLength = CHAT_TEXT_MAXLEN;
  chatAddInput.placeholder = `定型文を追加（最大${CHAT_TEXT_MAXLEN}文字）`;
  const chatAddBtn = el('button', 'pool-btn pool-chat-add-btn', chatAddWrap);
  chatAddBtn.textContent = '追加';
  const renderChatPresets = (): void => {
    while (chatEditList.firstChild) chatEditList.removeChild(chatEditList.firstChild);
    if (chatPresets.length === 0) {
      const empty = el('div', 'pool-chat-empty', chatEditList);
      empty.textContent = '定型文がありません（下の欄から追加）';
    }
    chatPresets.forEach((text, i) => {
      const row = el('div', 'pool-row', chatEditList);
      const lab = el('span', 'pool-row-label', row);
      lab.textContent = text; // textContent（XSS防止）
      const del = el('button', 'pool-chat-del', row);
      del.textContent = '✕';
      del.setAttribute('aria-label', `「${text}」を削除`);
      del.addEventListener('click', () => {
        chatPresets.splice(i, 1);
        saveChatPresets();
        renderChatPresets();
        chatRow.sync();
      });
    });
    const full = chatPresets.length >= CHAT_PRESET_MAX;
    chatAddInput.disabled = full;
    chatAddBtn.disabled = full;
    chatAddInput.placeholder = full
      ? `登録上限（${CHAT_PRESET_MAX}件）に達しています`
      : `定型文を追加（最大${CHAT_TEXT_MAXLEN}文字）`;
  };
  const addChatPreset = (): void => {
    if (chatPresets.length >= CHAT_PRESET_MAX) return;
    const t = sanitizeChatText(chatAddInput.value);
    if (!t) return;
    chatPresets.push(t);
    saveChatPresets();
    chatAddInput.value = '';
    renderChatPresets();
    chatRow.sync();
  };
  chatAddBtn.addEventListener('click', addChatPreset);
  chatAddInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') addChatPreset();
  });
  renderChatPresets();

  // ---- チャット読み上げ サブページ ----
  // 自分の音声プロファイル（性別 + プリセット）を設定。送信メッセージに同梱され、相手側はこれで読み上げる。
  // 相手の声は「相手の設定」に従うため、本ページに「相手の声」欄は無い（=自分の声のみ設定）。
  const ttsPage = el('div', 'pool-settings-page', panel);
  pages.push(ttsPage);
  ttsPage.hidden = true;
  const ttsHeader = el('div', 'pool-settings-header', ttsPage);
  const ttsBack = el('button', 'pool-settings-back', ttsHeader);
  ttsBack.textContent = '‹ 戻る';
  ttsBack.addEventListener('click', () => {
    ttsRow.sync();
    showPage(mainPage);
  });
  const ttsTitle = el('span', 'pool-settings-title', ttsHeader);
  ttsTitle.textContent = 'チャット読み上げ';

  const ttsList = el('div', 'pool-list', ttsPage);

  // (1) ON/OFF トグル（チャットヘッダのトグルと同じ状態を共有）。
  const ttsEnableRow = addToggleRow(
    ttsList,
    '読み上げを有効にする',
    () => chatTts.isEnabled(),
    (v: boolean) => {
      chatTts.setEnabled(v);
      // 他方（チャットヘッダのトグル）の見た目を同期させる既存イベント。
      window.dispatchEvent(new CustomEvent('pool:chat-tts-changed'));
      ttsRow.sync();
    },
    '初回 ON にする操作（タップ）が iOS Safari の音声 unlock を兼ねます。',
  );

  // (2) 性別ラジオ（女性 / 男性）。
  const genderSection = el('div', 'pool-row pool-row-static', ttsList);
  const genderLabel = el('span', 'pool-row-label', genderSection);
  genderLabel.textContent = '性別';
  const genderGroup = el('div', 'pool-tts-radio-group', genderSection);
  type GenderChoice = { readonly value: Gender; readonly label: string };
  const GENDER_CHOICES: readonly GenderChoice[] = [
    { value: 'female', label: '女性' },
    { value: 'male', label: '男性' },
  ];
  const genderButtons: { btn: HTMLButtonElement; value: Gender }[] = [];
  const syncGenderButtons = (): void => {
    const cur = chatTts.getMyGender();
    for (const g of genderButtons) {
      g.btn.classList.toggle('is-on', g.value === cur);
    }
    ttsRow.sync();
  };
  // 設定変更時に自動的に「こんにちは」を発声するヘルパ（性別/プリセット/音声切替で即試聴）。
  const autoPreviewMyVoice = (): void => {
    chatTts.preview(chatTts.getMyVoiceProfile());
  };
  for (const g of GENDER_CHOICES) {
    const btn = el('button', 'pool-tts-radio', genderGroup) as HTMLButtonElement;
    btn.type = 'button';
    btn.textContent = g.label;
    btn.addEventListener('click', () => {
      chatTts.setMyGender(g.value);
      syncGenderButtons();
      // 性別を変えたらプリセット行の pitch 表示も更新（男声/女声で値が異なるため）。
      syncPresetRows();
      autoPreviewMyVoice();
    });
    genderButtons.push({ btn, value: g.value });
  }
  syncGenderButtons();

  // (2.5) 音声選択ドロップダウン（自動 / 利用可能な日本語ボイス一覧）。
  // 「自動」は性別から推定（既定）、明示選択でそのボイス固定。
  const voiceSection = el('div', 'pool-row pool-row-static', ttsList);
  const voiceLabelEl = el('span', 'pool-row-label', voiceSection);
  voiceLabelEl.textContent = '音声';
  const voiceSelect = el('select', 'pool-tts-voice-select', voiceSection) as HTMLSelectElement;
  const populateVoiceOptions = (): void => {
    const prev = voiceSelect.value;
    while (voiceSelect.firstChild) voiceSelect.removeChild(voiceSelect.firstChild);
    const autoOpt = el('option', '', voiceSelect) as HTMLOptionElement;
    autoOpt.value = '';
    autoOpt.textContent = '自動（性別から推定）';
    const voices = chatTts.getAvailableJaVoices();
    for (const v of voices) {
      const opt = el('option', '', voiceSelect) as HTMLOptionElement;
      opt.value = v.name;
      opt.textContent = `${v.name}${v.localService ? '' : ' [クラウド]'}`;
    }
    // 現在の選択を復元（保存値 → 直前値 → 自動）
    const saved = chatTts.getMyVoiceName() ?? '';
    if (Array.from(voiceSelect.options).some((o) => o.value === saved)) {
      voiceSelect.value = saved;
    } else if (Array.from(voiceSelect.options).some((o) => o.value === prev)) {
      voiceSelect.value = prev;
    } else {
      voiceSelect.value = '';
    }
  };
  populateVoiceOptions();
  // iOS Safari など voices 遅延ロード環境向けに、voiceschanged を捉えてリスト再生成。
  if ('speechSynthesis' in window) {
    try {
      window.speechSynthesis.addEventListener('voiceschanged', populateVoiceOptions);
    } catch {
      // 古い環境は addEventListener 非対応の可能性。既存 onvoiceschanged は chat-tts.ts 側が握っている。
    }
  }
  voiceSelect.addEventListener('change', () => {
    chatTts.setMyVoiceName(voiceSelect.value === '' ? null : voiceSelect.value);
    autoPreviewMyVoice();
  });

  // (3) プリセット選択（縦リスト・単一選択）。
  const presetSection = el('div', 'pool-row pool-row-static', ttsList);
  const presetLabel = el('span', 'pool-row-label', presetSection);
  presetLabel.textContent = '雰囲気（プリセット）';
  const presetList = el('div', 'pool-tts-preset-list', ttsList);
  const presetRows: { row: HTMLElement; value: PresetId }[] = [];
  // 性別変更時に各行の pitch 表示を再描画するためのフック集約。
  const presetRowsRedrawHooks: Array<() => void> = [];
  const syncPresetRows = (): void => {
    const cur = chatTts.getMyPresetId();
    for (const p of presetRows) {
      p.row.classList.toggle('is-selected', p.value === cur);
    }
    for (const hook of presetRowsRedrawHooks) hook();
    ttsRow.sync();
  };
  for (const preset of chatTts.getPresets() as readonly VoicePreset[]) {
    const row = el('div', 'pool-row is-drill pool-tts-preset-row', presetList);
    const lab = el('span', 'pool-row-label', row);
    lab.textContent = preset.label;
    const meta = el('span', 'pool-row-value', row);
    // 行に表示するピッチは現在の性別側を反映（女性/男性で値が異なるため）。
    const renderMeta = (): void => {
      const p = chatTts.getMyGender() === 'male' ? preset.malePitch : preset.femalePitch;
      meta.textContent = `速度 ${preset.rate.toFixed(2)} / ピッチ ${p.toFixed(2)}`;
    };
    renderMeta();
    const check = el('span', 'pool-row-check', row);
    check.textContent = '✓';
    row.addEventListener('click', () => {
      chatTts.setMyPresetId(preset.id);
      syncPresetRows();
      autoPreviewMyVoice();
    });
    presetRows.push({ row, value: preset.id });
    // 性別変更で表示メタも更新する必要があるため、後段で再描画フックを束ねる。
    presetRowsRedrawHooks.push(renderMeta);
  }
  syncPresetRows();

  // (4) カスタム音声プロファイル（1スロット・pitch/rate スライダー）。
  // 行をタップすると「カスタム」が選択され、下にスライダーエディタが展開される。
  // スライダー操作中も自動プレビュー（ドラッグ完了 = change イベント）。
  const customRow = el('div', 'pool-row is-drill pool-tts-preset-row pool-tts-custom-row', presetList);
  const customLab = el('span', 'pool-row-label', customRow);
  customLab.textContent = CUSTOM_PRESET_LABEL;
  const customMeta = el('span', 'pool-row-value pool-tts-custom-meta', customRow);
  const customCheck = el('span', 'pool-row-check', customRow);
  customCheck.textContent = '✓';
  const renderCustomMeta = (): void => {
    customMeta.textContent = `速度 ${chatTts.getCustomRate().toFixed(2)} / ピッチ ${chatTts.getCustomPitch().toFixed(2)}`;
  };
  renderCustomMeta();
  customRow.addEventListener('click', () => {
    chatTts.setMyPresetId('custom');
    syncPresetRows();
    autoPreviewMyVoice();
  });
  presetRows.push({ row: customRow, value: 'custom' });
  presetRowsRedrawHooks.push(renderCustomMeta);

  // カスタム編集パネル（スライダー2つ・カスタム選択中のみ表示）。
  const customEditor = el('div', 'pool-tts-custom-editor', ttsPage);
  const makeSlider = (
    labelText: string,
    min: number,
    max: number,
    step: number,
    initial: number,
    onChange: (v: number) => void,
  ): { input: HTMLInputElement; valueLabel: HTMLElement; sync: () => void } => {
    const row = el('div', 'pool-tts-slider-row', customEditor);
    const lab = el('span', 'pool-tts-slider-label', row);
    lab.textContent = labelText;
    const input = el('input', 'pool-tts-slider', row) as HTMLInputElement;
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(initial);
    const valueLabel = el('span', 'pool-tts-slider-value', row);
    valueLabel.textContent = initial.toFixed(2);
    const sync = (): void => {
      input.value = String(initial); // ※ 呼び出し側で input.value を更新するためここは初期反映用のみ
    };
    // スライダー操作中（input イベント）: 値ラベルだけ即時更新（軽い）
    input.addEventListener('input', () => {
      valueLabel.textContent = Number(input.value).toFixed(2);
    });
    // スライダーから手を離した時（change イベント）: 永続化＋プレビュー再生
    input.addEventListener('change', () => {
      onChange(Number(input.value));
      // 'custom' を確定選択（他プリセットが選ばれていた場合のためにカスタムへ切替）
      chatTts.setMyPresetId('custom');
      syncPresetRows();
      renderCustomMeta();
      autoPreviewMyVoice();
    });
    return { input, valueLabel, sync };
  };
  const pitchSlider = makeSlider(
    'ピッチ',
    CUSTOM_PITCH_MIN,
    CUSTOM_PITCH_MAX,
    0.05,
    chatTts.getCustomPitch(),
    (v) => chatTts.setCustomPitch(v),
  );
  const rateSlider = makeSlider(
    '速度',
    CUSTOM_RATE_MIN,
    CUSTOM_RATE_MAX,
    0.05,
    chatTts.getCustomRate(),
    (v) => chatTts.setCustomRate(v),
  );
  // 「カスタム」選択中のみ展開（折りたたみ）。
  const syncCustomEditorVisibility = (): void => {
    const isCustom = chatTts.getMyPresetId() === 'custom';
    customEditor.classList.toggle('is-open', isCustom);
  };
  syncCustomEditorVisibility();
  // syncPresetRows に折りたたみ更新を連動。
  presetRowsRedrawHooks.push(syncCustomEditorVisibility);
  // 抑止: lint 用に参照を保持（実機では input/sync は使わないが将来再利用想定）
  void pitchSlider;
  void rateSlider;

  // 補足説明（注意書き）。
  const ttsNote = el('div', 'pool-tts-note', ttsPage);
  ttsNote.textContent =
    '※ 送信メッセージには自分の音声プロファイルが同梱され、相手は相手の端末で「相手の設定」で読み上げます。設定変更時に自動で「こんにちは」を再生します。';

  // 「読み上げを有効」トグルの状態は他方からも変わりうる（チャットヘッダの 🔈/🔊）。
  // カスタムイベントで同期する。
  window.addEventListener('pool:chat-tts-changed', () => {
    ttsEnableRow.sync();
    ttsRow.sync();
  });

  // ---- 設定の初期化 サブページ ----
  // 基本リセット（ON/OFF 系・必ず実行）と、チェックボックスで選んだ追加項目を localStorage から削除する。
  // 削除後は location.reload() で再起動し、各モジュールの module-scope 変数を確実に初期化する。
  // ニックネーム・成績・インベントリ・ドリル進捗・CPUプレイヤー定義は対象外。
  const RESET_BASIC_KEYS: readonly string[] = [
    'pool.sound',
    'pool.autoTopAfterShot',
    'pool.autoTopOnObscured',
    'pool.shotFreezeCam',
    'pool.autoViewReset',
    'pool.autoViewResetZoom',
    'pool.ballRoster',
    'pool.controlUiVisible',
    'pool.autoAim',
    'pool.breakSpread',
    'pool.threePointBreak',
    'pool.reflectGuide',
    'pool.cutAngleGuide',
    'pool.dragGuide',
  ];
  interface ResetOption {
    readonly label: string;
    readonly description: string;
    readonly keys: readonly string[];
    /** 特殊削除ロジック（複合キー削除など）。keys と排他ではなく追加実行される。 */
    readonly extraClear?: () => void;
  }
  const RESET_OPTIONS: readonly ResetOption[] = [
    {
      label: 'ショット入力方法',
      description: '「引いて撞く」/「タイミング撞き」の選択を既定（引いて撞く）に戻します。',
      keys: ['pool.shotInput'],
    },
    {
      label: 'ビューアングル自動戻し秒数',
      description: '秒数選択（0.5/1/2/3 秒）を既定の 0.5 秒に戻します。',
      keys: ['pool.autoViewResetSec'],
    },
    {
      label: 'テーブルコンディション（湿度）',
      description: 'クロスとクッションの摩擦設定を既定（標準）に戻します。',
      keys: ['pool.humidity'],
    },
    {
      label: 'キューの飛び（スクワート）',
      description: '撞点ズレ時の飛び量を既定（Lv 5 / 10）に戻します。',
      keys: ['pool.squirtLevel'],
    },
    {
      label: 'UIサイズ倍率',
      description: '体感調整倍率を既定（1.0×）に戻します。',
      keys: ['pool.uiUserScale'],
    },
    {
      label: 'プレイモード',
      description: '選択中のモード（9/10/8ボール・プラクティス）を既定（9ボール）に戻します。',
      keys: ['pool.playMode'],
    },
    {
      label: 'ラシャの色',
      description: 'テーブル表面の色をトーナメント緑に戻します。',
      keys: ['pool.feltColor'],
    },
    {
      label: 'チャット定型文',
      description: '登録済みの最大10件を既定の10件に戻します。',
      keys: ['pool.chatPresets'],
    },
    {
      label: 'チャット読み上げ（TTS）',
      description: 'ON/OFF・性別・声プリセット・カスタム値を全てクリアします。',
      keys: [
        'pool.chatTtsEnabled',
        'pool.chatTtsMeGender',
        'pool.chatTtsMePresetId',
        'pool.chatTtsCustomPitch',
        'pool.chatTtsCustomRate',
        'pool.chatTtsMeVoiceName',
      ],
    },
    {
      label: 'ラック調整',
      description: '各モードの球位置オフセット・タブ構成を全て削除します。',
      keys: ['pool.rackv2.nineBall', 'pool.rackv2.tenBall', 'pool.rackv2.eightBall'],
    },
    {
      label: 'ブレイク手玉位置記憶',
      description: '各モードのお気に入りブレイク手玉位置を全て削除します（既定位置に戻る）。',
      keys: [],
      extraClear: () => clearBreakCuePos(),
    },
    {
      label: 'HUDレイアウト',
      description: '各HUDのドラッグ移動位置を初期配置に戻します。',
      keys: ['pool.hud.positions.v2'],
    },
  ];

  const resetPage = el('div', 'pool-settings-page', panel);
  pages.push(resetPage);
  resetPage.hidden = true;
  const resetHeader = el('div', 'pool-settings-header', resetPage);
  const resetBack = el('button', 'pool-settings-back', resetHeader);
  resetBack.textContent = '‹ 戻る';
  resetBack.addEventListener('click', () => showPage(mainPage));
  const resetTitle = el('span', 'pool-settings-title', resetHeader);
  resetTitle.textContent = '設定の初期化';
  const resetBody = el('div', 'pool-list', resetPage);

  // 基本リセット説明（情報のみ・チェック不要）
  const basicInfo = el('div', 'pool-row pool-reset-info', resetBody);
  const basicInfoText = el('div', 'pool-reset-info-text', basicInfo);
  basicInfoText.textContent =
    `必ず初期化される項目（${RESET_BASIC_KEYS.length} 件のON/OFF設定）: ` +
    '効果音 / ショット後にトップビュー / 対象球が隠れたら自動トップビュー / ' +
    '狙撃カム / ビューアングル自動戻し（含ズーム） / 残球表示 / ' +
    '方向アロー・ズームボタン / 自動照準 / ブレイクのばらつき / ' +
    '3ポイントルール / 軌跡ガイド / カット角ガイド / ドラッグ位置ガイド';

  // オプションリセット見出し
  const optHead = el('div', 'pool-row pool-reset-section', resetBody);
  optHead.textContent = '追加でリセットする項目（チェックして選択）';

  // オプション項目（チェックボックス）
  const checkedOptions = new Set<number>(); // index ベースで選択管理
  for (let i = 0; i < RESET_OPTIONS.length; i++) {
    const opt = RESET_OPTIONS[i]!;
    const row = el('div', 'pool-row pool-reset-option', resetBody);
    const labelWrap = el('label', 'pool-reset-option-label', row);
    const chk = document.createElement('input');
    chk.type = 'checkbox';
    chk.className = 'pool-reset-checkbox';
    chk.checked = false;
    labelWrap.appendChild(chk);
    const text = el('span', 'pool-reset-option-text', labelWrap);
    text.textContent = opt.label;
    const desc = el('div', 'pool-reset-option-desc', row);
    desc.textContent = opt.description;
    chk.addEventListener('change', () => {
      if (chk.checked) checkedOptions.add(i);
      else checkedOptions.delete(i);
    });
  }

  // 実行ボタン
  const execRow = el('div', 'pool-row pool-reset-exec', resetBody);
  const execBtn = el('button', 'pool-reset-button', execRow);
  execBtn.textContent = '初期化を実行';
  execBtn.addEventListener('click', () => {
    const extras = Array.from(checkedOptions)
      .map((i) => RESET_OPTIONS[i]!.label)
      .join('、');
    const extrasMsg = extras ? `\n追加で削除: ${extras}` : '';
    const ok = window.confirm(
      `ON/OFF設定（${RESET_BASIC_KEYS.length} 件）を初期値に戻します。${extrasMsg}\n\n実行後はページが再読み込みされます。続行しますか？`,
    );
    if (!ok) return;
    try {
      for (const k of RESET_BASIC_KEYS) localStorage.removeItem(k);
      for (const i of checkedOptions) {
        const opt = RESET_OPTIONS[i]!;
        for (const k of opt.keys) localStorage.removeItem(k);
        opt.extraClear?.();
      }
    } catch (err) {
      console.error('設定初期化: localStorage 削除失敗:', err);
      window.alert('一部の項目削除に失敗しましたが、再読み込みを試みます。');
    }
    // location.reload() は同期的に動かない可能性があるためマイクロタスクで実行
    setTimeout(() => {
      location.reload();
    }, 50);
  });

  // ---- ライセンス情報 サブページ ----
  const licensePage = el('div', 'pool-settings-page', panel);
  pages.push(licensePage);
  licensePage.hidden = true;
  const licHeader = el('div', 'pool-settings-header', licensePage);
  const licBack = el('button', 'pool-settings-back', licHeader);
  licBack.textContent = '‹ 戻る';
  licBack.addEventListener('click', () => showPage(mainPage));
  const licTitle = el('span', 'pool-settings-title', licHeader);
  licTitle.textContent = 'ライセンス情報';
  const licBody = el('div', 'pool-settings-license', licensePage);

  const licLine = (text: string): void => {
    const p = el('p', 'pool-license-line', licBody);
    p.textContent = text;
  };
  const licLink = (label: string, url: string): void => {
    const a = el('a', 'pool-license-link', licBody);
    a.textContent = label;
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
  };

  licLine(
    '本アプリは GNU General Public License v3.0 (GPL-3.0) で頒布されます。GPL の物理エンジンを含むため、アプリ全体が GPL-3.0 の派生物です。',
  );
  licLine('利用しているオープンソース:');
  licLine('• 物理エンジン: tailuge/billiards（GPL-3.0・改変利用）');
  licLink('  → github.com/tailuge/billiards', 'https://github.com/tailuge/billiards');
  licLine('• 3D 描画: three.js（MIT）');
  licLink('  → github.com/mrdoob/three.js', 'https://github.com/mrdoob/three.js');
  licLine(
    '• 物理モデル（学術）: Han (2005) / Mathavan / Alciatore / Kiefl 補正',
  );
  licLine(
    'GPL-3.0 に基づき、本アプリの対応するソースコードを受け取る権利があります。ソースの入手は配布者にお問い合わせください。',
  );
  licLink('GPL-3.0 全文', 'https://www.gnu.org/licenses/gpl-3.0.html');

  // 対戦/観戦の開始・終了時にロック対象行を再同期する。
  // - autoAim 行: 対戦中はグレーアウト＋強制 OFF 表示。
  // - プレイモード行: 対戦中はグレーアウト＋タップ無視（モード変更を抑止）。
  //   対戦終了で自動的にロック解除＝ユーザーが選んだモードへ切替可能に戻る。
  onMatchActiveChange = (): void => {
    autoAimRow.sync();
    modeRow.sync();
  };

  showPage(mainPage);

  return {
    toggle: () => setOpen(panel.hidden),
  };
};
